import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "../../../ui/components/Dialog";
import { Button } from "../../../ui/components/Button";
import { IconButton } from "../../../ui/components/IconButton";
import { TextArea } from "../../../ui/components/TextArea";
import { FeatherX, FeatherFile, FeatherDownload } from "@subframe/core";
import {
  createReviewRequest,
  CreateReviewRequestDTO,
  ChangeDetails,
} from "../../utils/reviewWorkflow";
import { useToast } from "./Toast";
import { Transaction } from "../../../services/api";
import { useTaxes } from "../hooks/useTaxes";
import {
  formatInvoiceBillAllowlistedCreateSummaryLines,
  formatInvoiceBillFieldChangesForApprovalList,
  formatInvoiceBillReviewSummarySections,
} from "../../utils/review";
import {
  REVIEW_ACTION_LABELS,
  sortActionTypesForDisplay,
} from "../../utils/review/reviewActionLabels";
import { getEffectiveTotalFromRawJson } from "../../utils/review/invoiceBillEffectiveTotal";

export { getEffectiveTotalFromRawJson };

// ============================================================================
// EXPORTED FORMATTING FUNCTIONS (for use in ReviewStatusBanner)
// ============================================================================

// Helper: Convert dates from YYYY-MM-DD to DD-MM-YYYY
const convertDateFormat = (dateStr: string): string => {
  // Check if it's a date in YYYY-MM-DD format
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateRegex.test(dateStr)) {
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  }
  return dateStr;
};

// Helper: Format snake_case values to readable format
const formatValue = (val: string): string => {
  if (!val || val === "empty") return val;
  const strVal = String(val);

  // Check if it's a date and convert format
  const convertedDate = convertDateFormat(strVal);
  if (convertedDate !== strVal) {
    return convertedDate;
  }

  if (
    strVal.includes("_") &&
    !strVal.startsWith("[") &&
    !strVal.startsWith("{")
  ) {
    return strVal
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  // Always capitalize first letter for single words
  return strVal.charAt(0).toUpperCase() + strVal.slice(1).toLowerCase();
};

// Fields whose values should always be displayed in UPPERCASE (country, place of supply, etc.)
const isCountryOrSupplyField = (fieldName: string): boolean => {
  const lower = (fieldName || "").toLowerCase().replace(/\s+/g, "_");
  return (
    lower === "country" ||
    lower.includes("place_of_supply") ||
    lower.includes("destination_of_supply") ||
    lower.includes("source_of_supply")
  );
};

// Currency fields: display code/symbol in UPPERCASE (e.g. INR, USD)
const isCurrencyField = (fieldName: string): boolean => {
  const lower = (fieldName || "").toLowerCase().replace(/\s+/g, "_");
  return (
    lower === "currency_code" ||
    lower === "currency_symbol" ||
    lower === "currency"
  );
};

// Tax Name field (line item): empty → "No Tax", always UPPERCASE
const isTaxNameField = (fieldName: string): boolean => {
  const lower = (fieldName || "").toLowerCase();
  return lower.includes("tax_name") || lower.includes("tax name");
};

// Format value for display; uppercases for country, supply, currency; Tax Name: empty → "No Tax", uppercase
const formatValueForDisplay = (val: string, fieldName?: string): string => {
  if (fieldName && isTaxNameField(fieldName)) {
    if (!val || val === "empty" || String(val).trim() === "") return "No Tax";
    return String(val).toUpperCase();
  }

  const formatted = formatValue(val);
  if (fieldName && isCountryOrSupplyField(fieldName)) {
    return formatted.toUpperCase();
  }
  if (fieldName && isCurrencyField(fieldName)) {
    return formatted.toUpperCase();
  }
  return formatted;
};

const idFieldsToHideInLineItems = [
  "customer_id",
  "vendor_id",
  "account_id",
  "contact_id",
  "tax_id",
  "invoice_id",
  "bill_id",
  "item_id",
  "product_id",
  "tds_tax_id",
  "_id",
  "id",
  "type", // Internal Zoho line item type (e.g. 1) - not a user-facing form field
];

/** Snake_case or camelCase to Title Case for display */
const formatLineItemFieldName = (key: string): string => {
  const withSpaces = key.replace(/_/g, " ");
  return withSpaces
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/** Ordered keys and display labels for credit/debit note line items (no internal "type" field). */
const CREDIT_DEBIT_LINE_ITEM_DISPLAY_KEYS: Array<{
  key: string;
  label: string;
}> = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "account_name", label: "Account Name" },
  { key: "quantity", label: "Quantity" },
  { key: "rate", label: "Rate" },
  { key: "tax_name", label: "Tax Name" },
  { key: "hsn_or_sac", label: "HSN Or SAC" },
  { key: "tax_percentage", label: "Tax Percentage" },
  { key: "tax_amount", label: "Tax Amount" },
  { key: "item_total", label: "Item Total" },
  { key: "item_total_inclusive_of_tax", label: "Item Total Inclusive Of Tax" },
  { key: "tds_tax_name", label: "TDS Tax Name" },
  { key: "tds_tax_percentage", label: "TDS Tax Percentage" },
  { key: "tds_tax_amount", label: "TDS Tax Amount" },
];

/**
 * Format ALL line items from raw_json for display (invoice, bill, credit_note, debit_note).
 * For credit_note/debit_note uses a fixed order and friendly labels; excludes internal "type" field.
 * Shows every line item with full details even if the user did not change them.
 */
export const formatAllLineItemsFromRawJson = (
  rawJson: any,
  moduleCode?: string,
): string[] => {
  if (!rawJson?.line_items || !Array.isArray(rawJson.line_items)) {
    return [];
  }
  const moduleLower = (moduleCode || "").toLowerCase();
  const isInvoiceBillCreditDebit =
    moduleLower === "invoice" ||
    moduleLower === "bill" ||
    moduleLower === "credit_note" ||
    moduleLower === "debit_note";

  if (!isInvoiceBillCreditDebit) {
    return [];
  }

  const isCreditOrDebit =
    moduleLower === "credit_note" || moduleLower === "debit_note";

  const lines: string[] = [];
  lines.push("");
  lines.push("Line Items:");

  rawJson.line_items.forEach((item: any, index: number) => {
    const lineNum = index + 1;
    lines.push(`  Line Item ${lineNum}:`);

    const keysToShow = isCreditOrDebit
      ? CREDIT_DEBIT_LINE_ITEM_DISPLAY_KEYS
      : Object.keys(item || {})
          .filter((key) => {
            const lower = key.toLowerCase();
            if (idFieldsToHideInLineItems.some((idF) => lower.includes(idF)))
              return false;
            if (lower.endsWith("_id") || lower === "id") return false;
            return true;
          })
          .map((key) => ({ key, label: formatLineItemFieldName(key) }));

    keysToShow.forEach(({ key, label }) => {
      const val = item[key];
      if (val === undefined || val === null) return;
      const strVal =
        typeof val === "object" ? JSON.stringify(val) : String(val);
      const displayVal = formatValueForDisplay(strVal, key);
      lines.push(`    ${label}: ${displayVal}`);
    });
    lines.push("");
  });

  return lines;
};

/** Ordered header keys and labels for credit/debit note document summary. */
const CREDIT_DEBIT_HEADER_DISPLAY_KEYS: Array<{ key: string; label: string }> =
  [
    { key: "date", label: "Date" },
    { key: "customer_name", label: "Customer Name" },
    { key: "vendor_name", label: "Vendor Name" },
    { key: "creditnote_number", label: "Credit Note Number" },
    { key: "place_of_supply", label: "Place Of Supply" },
    { key: "gst_treatment", label: "GST Treatment" },
    { key: "gst_no", label: "GST No" },
    { key: "currency_code", label: "Currency" },
    { key: "currency_symbol", label: "Currency Symbol" },
    { key: "reference_number", label: "Reference Number" },
    { key: "notes", label: "Notes" },
    { key: "narration", label: "Narration" },
    { key: "sub_total", label: "Sub Total" },
    { key: "tax_total", label: "Tax Total" },
    { key: "total", label: "Total" },
  ];

/**
 * Format credit/debit note header (document) fields from raw_json for the change summary.
 * Excludes line_items and internal IDs so the summary shows all form-relevant entries.
 * Exported for use in ApprovalDetailPanel and ApprovalDetailViewPanel.
 */
export const formatCreditDebitNoteHeaderFromRawJson = (
  rawJson: any,
): string[] => {
  if (!rawJson || typeof rawJson !== "object") return [];
  const lines: string[] = [];
  lines.push("Document:");
  CREDIT_DEBIT_HEADER_DISPLAY_KEYS.forEach(({ key, label }) => {
    const val = rawJson[key];
    if (val === undefined || val === null || val === "") return;
    const strVal = typeof val === "object" ? JSON.stringify(val) : String(val);
    const displayVal = formatValueForDisplay(strVal, key);
    lines.push(`  ${label}: ${displayVal}`);
  });
  return lines.length > 1 ? lines : [];
};

/**
 * Format invoice/bill create summary for display (send for review + approval).
 * Shows: document details (customer/vendor name, number, dates, GSTINs), line items
 * (description, account, HSN, qty, rate, GST, amounts), and total breakdown.
 * Use only for Create action on manual invoice/bill.
 */
export const formatInvoiceBillCreateSummaryFromRawJson = (
  rawJson: any,
  moduleCode: string,
): string[] => {
  if (!rawJson || typeof rawJson !== "object") return [];
  const moduleLower = (moduleCode || "").toLowerCase();
  const isInvoice = moduleLower === "invoice";
  const isBill = moduleLower === "bill";
  if (!isInvoice && !isBill) return [];

  const lines: string[] = [];
  const fmtDate = (d: string | null | undefined) =>
    d ? convertDateFormat(String(d)) : "";
  const fmtNum = (n: number | null | undefined) => (n != null ? String(n) : "");
  const fmtStr = (s: string | null | undefined) =>
    s != null && String(s).trim() !== "" ? String(s).trim() : null;

  // --- Document details ---
  lines.push("Document details:");
  if (isInvoice) {
    const customerName = fmtStr(rawJson.customer_name);
    if (customerName) lines.push(`  Customer: ${customerName}`);
  } else {
    const vendorName = fmtStr(rawJson.vendor_name);
    if (vendorName) lines.push(`  Vendor: ${vendorName}`);
  }
  const docNumber = isInvoice
    ? fmtStr(rawJson.invoice_number)
    : fmtStr(rawJson.bill_number) || fmtStr(rawJson.invoice_number);
  if (docNumber)
    lines.push(`  ${isInvoice ? "Invoice" : "Bill"} Number: ${docNumber}`);
  const date = fmtDate(rawJson.date || rawJson.invoice_date);
  if (date) lines.push(`  Date: ${date}`);
  const dueDate = fmtDate(rawJson.due_date);
  if (dueDate) lines.push(`  Due Date: ${dueDate}`);
  const vendorGstin = fmtStr(rawJson.vendor_gstin) || fmtStr(rawJson.gst_no);
  if (vendorGstin) lines.push(`  Vendor GSTIN: ${vendorGstin}`);
  const customerGstin = fmtStr(rawJson.customer_gstin);
  if (customerGstin) lines.push(`  Customer GSTIN: ${customerGstin}`);
  lines.push("");

  // --- Line items ---
  const items = rawJson.line_items;
  if (items && Array.isArray(items) && items.length > 0) {
    lines.push("Line items:");
    items.forEach((item: any, index: number) => {
      const lineNum = index + 1;
      lines.push(`  Line Item ${lineNum}:`);
      const desc = fmtStr(item.description) || fmtStr(item.name) || "—";
      lines.push(`    Description: ${desc}`);
      const accountName = fmtStr(item.account_name) || "—";
      if (isInvoice) {
        lines.push(`    Debit A/c: Accounts Receivable`);
        lines.push(`    Credit A/c: ${accountName}`);
      } else {
        lines.push(`    Debit A/c: ${accountName}`);
        lines.push(`    Credit A/c: Accounts Payable`);
      }
      const hsn = fmtStr(item.hsn_or_sac);
      lines.push(`    HSN Code: ${hsn || "Not given"}`);
      lines.push(`    Quantity: ${fmtNum(item.quantity) || "—"}`);
      lines.push(`    Rate: ${fmtNum(item.rate) || "—"}`);
      const taxName = fmtStr(item.tax_name);
      const taxPct =
        item.tax_percentage != null ? `${item.tax_percentage}%` : "";
      const gstDisplay = taxName
        ? taxPct
          ? `${taxName} (${taxPct})`
          : taxName
        : "No Tax";
      lines.push(`    GST: ${gstDisplay}`);
      const amountWo = item.item_total != null ? String(item.item_total) : "—";
      lines.push(`    Amount without tax: ${amountWo}`);
      const amountWith =
        item.item_total_inclusive_of_tax != null
          ? String(item.item_total_inclusive_of_tax)
          : "—";
      lines.push(`    Amount including tax: ${amountWith}`);
      // For bills: show TDS per line when applied
      if (isBill && item.tds_tax_id && (item.tds_tax_percentage ?? 0) > 0) {
        const lineAmount = (item.rate ?? 0) * (item.quantity ?? 0);
        const tdsAmount =
          (lineAmount * (Number(item.tds_tax_percentage) || 0)) / 100;
        const tdsName = fmtStr(item.tds_tax_name) || "TDS";
        lines.push(
          `    TDS: ${tdsName} (${item.tds_tax_percentage}%) - ${tdsAmount.toFixed(2)}`,
        );
      }
      lines.push("");
    });
  }

  // --- Total breakdown ---
  lines.push("Total amount breakdown:");
  const subTotal = rawJson.sub_total ?? 0;
  const taxTotal = rawJson.tax_total ?? 0;
  lines.push(`  Subtotal: ${subTotal}`);
  lines.push(`  Tax Total: ${taxTotal}`);
  const tcsTaxId = rawJson.tcs_tax_id;
  const tcsPct = Number(rawJson.tcs_tax_percentage) || 0;
  const isTcsApplied = !!(tcsTaxId && tcsTaxId !== "" && tcsPct > 0);
  let tcsTotal = 0;
  if (isTcsApplied) {
    const baseForTcs = subTotal + taxTotal;
    tcsTotal = baseForTcs * (tcsPct / 100);
    const tcsName = fmtStr(rawJson.tcs_tax_name);
    if (tcsName) {
      lines.push(`  TCS Total (${tcsName}): ${tcsTotal.toFixed(2)}`);
    } else {
      lines.push(`  TCS Total: ${tcsTotal.toFixed(2)}`);
    }
  }
  // Gross total = subtotal + tax + TCS (when applied). For bills always use this so TDS/Amount Payable are correct.
  // const grossTotal = subTotal + taxTotal + tcsTotal;
  // const finalTotal = isBill
  //   ? grossTotal
  //   : isTcsApplied
  //     ? grossTotal
  //     : rawJson.total;

  const grossTotal = subTotal + taxTotal + tcsTotal;
  const finalTotal = grossTotal;

  if (finalTotal != null) {
    lines.push(
      `  Total: ${typeof finalTotal === "number" ? finalTotal.toFixed(2) : finalTotal}`,
    );
  }
  // For bills: show TDS Deducted and Amount Payable when any line has TDS (use derived gross total; TCS and TDS are mutually exclusive)
  if (isBill && items && Array.isArray(items) && items.length > 0) {
    const totalTDS = items.reduce((sum: number, item: any) => {
      if (
        item.tds_tax_percentage != null &&
        (item.tds_tax_percentage as number) > 0
      ) {
        const lineAmount = (item.rate ?? 0) * (item.quantity ?? 0);
        return (
          sum + (lineAmount * (Number(item.tds_tax_percentage) || 0)) / 100
        );
      }
      return sum;
    }, 0);
    if (totalTDS > 0) {
      lines.push(`  TDS Deducted: ${totalTDS.toFixed(2)}`);
      lines.push(`  Amount Payable: ${(grossTotal - totalTDS).toFixed(2)}`);
    }
  }

  return lines;
};

/**
 * Format field changes for Journal Entries
 * Exported for use in ReviewStatusBanner to ensure consistent formatting
 *
 * IMPORTANT: Only shows changes from field_changes array (not from raw_json)
 * Uses numeric comparison for IDs and amounts
 *
 * @param fieldChanges - Array of field changes from the review request
 * @param rawJson - Optional new JSON for reference (used to get new account/contact names)
 * @param originalJson - Optional original JSON for reference (used to get old account/contact names)
 */
export const formatJournalChangesForBanner = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
  rawJson?: any,
  originalJson?: any,
): string[] => {
  // Debug: Log field_changes structure for user visibility
  const changes: string[] = [];

  // Helper to compare numbers (for IDs and amounts)
  const compareNumbers = (a: string, b: string): boolean => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);
    if (isNaN(numA) && isNaN(numB)) return true;
    if (isNaN(numA) || isNaN(numB)) return false;
    return Math.abs(numA - numB) < 0.01; // Allow small floating point differences
  };

  // Helper to normalize empty values
  const normalizeValue = (val: string): string => {
    if (!val || val === "null" || val === "undefined" || val === "(empty)") {
      return "";
    }
    return val.trim();
  };

  // Helper: skip displaying (empty), "", 0, 0.00 - only show actual values
  const shouldSkipDisplay = (val: string): boolean => {
    if (!val || val.trim() === "") return true;
    if (val === "(empty)" || val === "empty") return true;
    const n = parseFloat(val);
    return !isNaN(n) && Math.abs(n) < 0.01; // 0 or 0.00
  };

  // Helper: capitalize first character for display
  const capitalizeForDisplay = (val: string): string => {
    if (!val || val.trim() === "") return val;
    return val.charAt(0).toUpperCase() + val.slice(1).toLowerCase();
  };

  // Helper to get account name from JSON by account_id
  const getAccountName = (
    accountId: string,
    useOriginal: boolean = false,
  ): string | null => {
    const jsonToSearch = useOriginal ? originalJson : rawJson;
    if (!jsonToSearch || !jsonToSearch.line_items || !accountId) return null;
    const lineItem = jsonToSearch.line_items.find(
      (item: any) => item.account_id?.toString() === accountId.toString(),
    );
    return lineItem?.account_name || null;
  };

  // Helper to get contact name from JSON by contact_id
  const getContactName = (
    contactId: string,
    useOriginal: boolean = false,
  ): string | null => {
    const jsonToSearch = useOriginal ? originalJson : rawJson;
    if (!jsonToSearch || !jsonToSearch.line_items || !contactId) return null;
    const lineItem = jsonToSearch.line_items.find(
      (item: any) => item.contact_id?.toString() === contactId.toString(),
    );
    return lineItem?.contact_name || null;
  };

  // Helper to format frequency from snake_case to readable text
  const formatFrequency = (frequency: string): string => {
    if (!frequency || frequency === "(empty)" || frequency === "empty") {
      return frequency || "(empty)";
    }

    const frequencyMap: Record<string, string> = {
      daily: "Daily",
      start_of_month: "Start of Month",
      end_of_month: "End of Month",
      end_of_quarter: "End of Quarter",
      end_of_year: "End of Year",
      date_of_month: "Date of Month",
      every_n_days: "Every N Days",
    };

    return frequencyMap[frequency.toLowerCase()] || formatValue(frequency);
  };

  // Track if this is a new journal (all old values are empty)
  const isNewJournal = fieldChanges.every((fc) => !normalizeValue(fc.old));

  // Group line item changes by index
  const lineItemChanges: Record<
    number,
    {
      account?: { old: string; new: string };
      contact?: { old: string; new: string };
      description?: { old: string; new: string };
      debit?: { old: string; new: string };
      credit?: { old: string; new: string };
      typeChanged?: { old: string; new: string };
      isAdded?: boolean;
      isRemoved?: boolean;
    }
  > = {};

  // Track recurring journal info
  const recurringInfo: {
    isRecurring?: { old: string; new: string };
    templateName?: { old: string; new: string };
    frequency?: { old: string; new: string };
    interval?: { old: string; new: string };
    startDate?: { old: string; new: string };
    endDate?: { old: string; new: string };
    neverExpires?: { old: string; new: string };
  } = {};

  // Process each field change
  fieldChanges.forEach((fc) => {
    const fieldLower = fc.field.toLowerCase();
    const normalizedOld = normalizeValue(fc.old);
    const normalizedNew = normalizeValue(fc.new);

    // Handle recurring journal fields
    if (fieldLower === "is_recurring" || fieldLower === "isrecurring") {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.isRecurring = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "template_name" ||
      (fieldLower.includes("recurring") && fieldLower.includes("name"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.templateName = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "recurrence_frequency" ||
      fieldLower === "recurrencefrequency" ||
      (fieldLower.includes("frequency") && fieldLower.includes("recur"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.frequency = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "recurrence_interval" ||
      fieldLower === "recurrenceinterval" ||
      (fieldLower.includes("interval") && fieldLower.includes("recur"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.interval = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "starts_on" ||
      fieldLower === "startson" ||
      (fieldLower.includes("start") && fieldLower.includes("date"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.startDate = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "ends_on" ||
      fieldLower === "endson" ||
      (fieldLower === "end_date" && fieldLower.includes("recur"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.endDate = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }
    if (
      fieldLower === "never_expires" ||
      fieldLower === "neverexpires" ||
      (fieldLower.includes("never") && fieldLower.includes("expire"))
    ) {
      if (normalizedOld !== normalizedNew || isNewJournal) {
        recurringInfo.neverExpires = {
          old: normalizedOld || "(empty)",
          new: normalizedNew || "(empty)",
        };
      }
      return;
    }

    // Handle line item changes: line_items[0].account_id, line_items[0].contact_id, line_items[0].amount, etc.
    const lineItemMatch = fc.field.match(
      /line_items\[(\d+)\]\.(account_id|contact_id|amount|is_debit_or_credit|description)/i,
    );
    if (lineItemMatch) {
      const index = parseInt(lineItemMatch[1], 10);
      const field = lineItemMatch[2].toLowerCase();

      if (!lineItemChanges[index]) {
        lineItemChanges[index] = {};
      }

      // Use numeric comparison for IDs and amounts
      const oldNum = normalizedOld ? parseFloat(normalizedOld) : NaN;
      const newNum = normalizedNew ? parseFloat(normalizedNew) : NaN;
      const isDifferent =
        isNaN(oldNum) || isNaN(newNum)
          ? normalizedOld !== normalizedNew
          : !compareNumbers(normalizedOld, normalizedNew);

      if (isDifferent || isNewJournal) {
        if (field === "account_id") {
          // Get account names: old from original_json, new from raw_json
          const oldAccountName = normalizedOld
            ? getAccountName(normalizedOld, true) || normalizedOld
            : "(empty)";
          const newAccountName = normalizedNew
            ? getAccountName(normalizedNew, false) || normalizedNew
            : "(empty)";
          lineItemChanges[index].account = {
            old: oldAccountName,
            new: newAccountName,
          };
        } else if (field === "contact_id") {
          // Get contact names: old from original_json, new from raw_json
          const oldContactName = normalizedOld
            ? getContactName(normalizedOld, true) || normalizedOld
            : "(empty)";
          const newContactName = normalizedNew
            ? getContactName(normalizedNew, false) || normalizedNew
            : "(empty)";
          lineItemChanges[index].contact = {
            old: oldContactName,
            new: newContactName,
          };
        } else if (field === "amount") {
          // Handle amount changes - determine if it's debit or credit
          // Check both original and current to handle type changes
          const oldItem = originalJson?.line_items?.[index];
          const newItem = rawJson?.line_items?.[index];

          const oldIsDebit = oldItem?.is_debit_or_credit === "debit";
          const newIsDebit = newItem?.is_debit_or_credit === "debit";

          // If type changed, we'll track both debit and credit (typeChanged will also be tracked separately)
          if (oldIsDebit !== newIsDebit) {
            // Type changed - track both old and new amounts
            if (oldIsDebit) {
              // Was debit, now credit
              lineItemChanges[index].debit = {
                old: normalizedOld || "0",
                new: "0",
              };
              lineItemChanges[index].credit = {
                old: "0",
                new: normalizedNew || "0",
              };
            } else {
              // Was credit, now debit
              lineItemChanges[index].credit = {
                old: normalizedOld || "0",
                new: "0",
              };
              lineItemChanges[index].debit = {
                old: "0",
                new: normalizedNew || "0",
              };
            }
          } else {
            // Type didn't change, just amount changed
            if (newIsDebit) {
              lineItemChanges[index].debit = {
                old: normalizedOld || "0",
                new: normalizedNew || "0",
              };
            } else {
              lineItemChanges[index].credit = {
                old: normalizedOld || "0",
                new: normalizedNew || "0",
              };
            }
          }
        } else if (field === "is_debit_or_credit") {
          // Handle debit/credit type change - this might also affect amount display
          // We'll track it separately but it's usually shown with amount
          // For now, we'll just note that the type changed
          if (normalizedOld !== normalizedNew) {
            // If type changed, we might need to swap debit/credit values
            // This is a complex case - for now, just track the type change
            lineItemChanges[index].typeChanged = {
              old: normalizedOld || "(empty)",
              new: normalizedNew || "(empty)",
            };
          }
        } else if (field === "description") {
          lineItemChanges[index].description = {
            old: normalizedOld || "(empty)",
            new: normalizedNew || "(empty)",
          };
        }
      }
      return;
    }

    // Handle "Line Item X" format (for added/removed items)
    const lineItemAddedRemovedMatch = fc.field.match(/^Line Item (\d+)$/i);
    if (lineItemAddedRemovedMatch) {
      const lineNumber = parseInt(lineItemAddedRemovedMatch[1], 10);
      const index = lineNumber - 1;

      if (!lineItemChanges[index]) {
        lineItemChanges[index] = {};
      }

      // Check if it's added or removed
      if (fc.old === "added" || normalizedOld === "added") {
        lineItemChanges[index].isAdded = true;
        // Try to extract information from the new value
        // Format: "Account Name - Amount (debit/credit)"
        if (fc.new && fc.new !== "added") {
          const parts = fc.new.split(" - ");
          if (parts.length > 0 && parts[0]) {
            // Account name (we'll try to get account_id from raw_json if available)
            lineItemChanges[index].account = {
              old: "",
              new: parts[0].trim(),
            };
          }
          // Try to extract amount and type
          if (parts.length > 1) {
            const amountAndType = parts[1].trim();
            // Format: "Amount (debit)" or "Amount (credit)"
            const typeMatch = amountAndType.match(/^([\d.]+)\s*\((\w+)\)$/);
            if (typeMatch) {
              const amount = typeMatch[1];
              const type = typeMatch[2].toLowerCase();
              if (type === "debit") {
                lineItemChanges[index].debit = { old: "", new: amount };
              } else if (type === "credit") {
                lineItemChanges[index].credit = { old: "", new: amount };
              }
            }
          }

          // Try to get account_name and contact_name from raw_json if available
          if (rawJson && rawJson.line_items && rawJson.line_items[index]) {
            const lineItem = rawJson.line_items[index];
            if (lineItem.account_name) {
              lineItemChanges[index].account = {
                old: "",
                new: lineItem.account_name,
              };
            }
            if (lineItem.contact_name) {
              lineItemChanges[index].contact = {
                old: "",
                new: lineItem.contact_name,
              };
            }
          }
        }
      } else if (fc.new === "removed" || normalizedNew === "removed") {
        lineItemChanges[index].isRemoved = true;
      }
      return;
    }

    // Skip raw snake_case field names (except those we've already handled)
    if (fc.field.includes("_") && !fc.field.startsWith("Line Item ")) {
      // But allow currency_id, reference_number, etc. to pass through
      if (
        fieldLower !== "currency_id" &&
        fieldLower !== "reference_number" &&
        fieldLower !== "notes" &&
        fieldLower !== "date" &&
        fieldLower !== "type"
      ) {
        return;
      }
    }

    // Handle regular journal fields
    let displayField = fc.field;
    if (fieldLower === "date") {
      displayField = "Date";
    } else if (
      fieldLower === "reference_number" ||
      fieldLower === "reference number"
    ) {
      displayField = "Reference Number";
    } else if (
      fieldLower === "currency_id" ||
      fieldLower === "currency_symbol"
    ) {
      displayField = "Currency";
    } else if (fieldLower === "notes") {
      displayField = "Notes";
    } else if (
      fieldLower === "type" ||
      fieldLower === "reporting_method" ||
      fieldLower === "reporting method"
    ) {
      displayField = "Reporting Method";
    }

    // Only show if there's an actual change; never show (empty) or 0.00
    if (isNewJournal || normalizedOld !== normalizedNew) {
      const formattedOld = normalizedOld ? formatValue(normalizedOld) : "";
      const formattedNew = normalizedNew ? formatValue(normalizedNew) : "";

      if (!normalizedOld || normalizedOld === "(empty)") {
        if (!shouldSkipDisplay(formattedNew)) {
          changes.push(
            `${displayField}: ${capitalizeForDisplay(formattedNew)}`,
          );
        }
      } else {
        const oldOk = !shouldSkipDisplay(formattedOld);
        const newOk = !shouldSkipDisplay(formattedNew);
        if (oldOk && newOk) {
          changes.push(
            `${displayField}: ${capitalizeForDisplay(
              formattedOld,
            )} → ${capitalizeForDisplay(formattedNew)}`,
          );
        } else if (newOk) {
          changes.push(
            `${displayField}: ${capitalizeForDisplay(formattedNew)}`,
          );
        } else if (oldOk) {
          changes.push(
            `${displayField}: ${capitalizeForDisplay(formattedOld)}`,
          );
        }
      }
    }
  });

  // Format grouped line item changes - sorted by line number
  const sortedLineItemIndices = Object.keys(lineItemChanges)
    .map((idx) => parseInt(idx, 10))
    .sort((a, b) => {
      const lineNumA = a + 1;
      const lineNumB = b + 1;
      return lineNumA - lineNumB;
    });

  sortedLineItemIndices.forEach((index) => {
    const itemChanges = lineItemChanges[index];
    const lineNumber = index + 1;

    const hasChanges =
      itemChanges.account ||
      itemChanges.contact ||
      itemChanges.description ||
      itemChanges.debit ||
      itemChanges.credit ||
      itemChanges.typeChanged ||
      itemChanges.isAdded ||
      itemChanges.isRemoved;

    if (!hasChanges) return;

    if (itemChanges.isRemoved) {
      changes.push(`Line Item ${lineNumber} Removed`);
      return;
    }

    if (itemChanges.isAdded) {
      changes.push(`Line Item ${lineNumber} Added =>`);

      if (
        itemChanges.account?.new &&
        !shouldSkipDisplay(itemChanges.account.new)
      ) {
        changes.push(
          `  Account: ${capitalizeForDisplay(itemChanges.account.new)}`,
        );
      }
      if (
        itemChanges.contact?.new &&
        !shouldSkipDisplay(itemChanges.contact.new)
      ) {
        changes.push(
          `  Contact: ${capitalizeForDisplay(itemChanges.contact.new)}`,
        );
      }
      if (itemChanges.debit?.new && !shouldSkipDisplay(itemChanges.debit.new)) {
        changes.push(`  Debit: ${capitalizeForDisplay(itemChanges.debit.new)}`);
      }
      if (
        itemChanges.credit?.new &&
        !shouldSkipDisplay(itemChanges.credit.new)
      ) {
        changes.push(
          `  Credit: ${capitalizeForDisplay(itemChanges.credit.new)}`,
        );
      }

      changes.push("");
    } else {
      // Only show fields that actually changed (use numeric comparison for IDs)
      const modifications: string[] = [];

      if (itemChanges.account) {
        const oldNum = itemChanges.account.old
          ? parseFloat(itemChanges.account.old)
          : NaN;
        const newNum = itemChanges.account.new
          ? parseFloat(itemChanges.account.new)
          : NaN;
        const isDifferent =
          isNaN(oldNum) || isNaN(newNum)
            ? itemChanges.account.old !== itemChanges.account.new
            : !compareNumbers(itemChanges.account.old, itemChanges.account.new);

        if (isDifferent) {
          const oldVal = itemChanges.account.old || "";
          const newVal = itemChanges.account.new || "";
          if (!shouldSkipDisplay(newVal)) {
            if (shouldSkipDisplay(oldVal)) {
              modifications.push(`  Account: ${capitalizeForDisplay(newVal)}`);
            } else {
              modifications.push(
                `  Account: ${capitalizeForDisplay(
                  oldVal,
                )} → ${capitalizeForDisplay(newVal)}`,
              );
            }
          } else if (!shouldSkipDisplay(oldVal)) {
            modifications.push(`  Account: ${capitalizeForDisplay(oldVal)}`);
          }
        }
      }

      if (itemChanges.contact) {
        const oldNum = itemChanges.contact.old
          ? parseFloat(itemChanges.contact.old)
          : NaN;
        const newNum = itemChanges.contact.new
          ? parseFloat(itemChanges.contact.new)
          : NaN;
        const isDifferent =
          isNaN(oldNum) || isNaN(newNum)
            ? itemChanges.contact.old !== itemChanges.contact.new
            : !compareNumbers(itemChanges.contact.old, itemChanges.contact.new);

        if (isDifferent) {
          const oldVal = itemChanges.contact.old || "";
          const newVal = itemChanges.contact.new || "";
          if (!shouldSkipDisplay(newVal)) {
            if (shouldSkipDisplay(oldVal)) {
              modifications.push(`  Contact: ${capitalizeForDisplay(newVal)}`);
            } else {
              modifications.push(
                `  Contact: ${capitalizeForDisplay(
                  oldVal,
                )} → ${capitalizeForDisplay(newVal)}`,
              );
            }
          } else if (!shouldSkipDisplay(oldVal)) {
            modifications.push(`  Contact: ${capitalizeForDisplay(oldVal)}`);
          }
        }
      }

      // Handle debit amount changes - skip 0.00
      if (itemChanges.debit) {
        const oldDebit = parseFloat(itemChanges.debit.old) || 0;
        const newDebit = parseFloat(itemChanges.debit.new) || 0;
        if (!compareNumbers(oldDebit.toString(), newDebit.toString())) {
          const oldStr = oldDebit.toFixed(2);
          const newStr = newDebit.toFixed(2);
          if (!shouldSkipDisplay(newStr)) {
            if (shouldSkipDisplay(oldStr)) {
              modifications.push(`  Debit: ${newStr}`);
            } else {
              modifications.push(`  Debit: ${oldStr} → ${newStr}`);
            }
          } else if (!shouldSkipDisplay(oldStr)) {
            modifications.push(`  Debit: ${oldStr}`);
          }
        }
      }

      // Handle credit amount changes - skip 0.00
      if (itemChanges.credit) {
        const oldCredit = parseFloat(itemChanges.credit.old) || 0;
        const newCredit = parseFloat(itemChanges.credit.new) || 0;
        if (!compareNumbers(oldCredit.toString(), newCredit.toString())) {
          const oldStr = oldCredit.toFixed(2);
          const newStr = newCredit.toFixed(2);
          if (!shouldSkipDisplay(newStr)) {
            if (shouldSkipDisplay(oldStr)) {
              modifications.push(`  Credit: ${newStr}`);
            } else {
              modifications.push(`  Credit: ${oldStr} → ${newStr}`);
            }
          } else if (!shouldSkipDisplay(oldStr)) {
            modifications.push(`  Credit: ${oldStr}`);
          }
        }
      }

      // Handle description changes - skip (empty)
      if (itemChanges.description) {
        const oldDesc = itemChanges.description.old || "";
        const newDesc = itemChanges.description.new || "";
        if (oldDesc !== newDesc) {
          if (!shouldSkipDisplay(newDesc)) {
            if (shouldSkipDisplay(oldDesc)) {
              modifications.push(
                `  Description: ${capitalizeForDisplay(newDesc)}`,
              );
            } else {
              modifications.push(
                `  Description: ${capitalizeForDisplay(
                  oldDesc,
                )} → ${capitalizeForDisplay(newDesc)}`,
              );
            }
          } else if (!shouldSkipDisplay(oldDesc)) {
            modifications.push(
              `  Description: ${capitalizeForDisplay(oldDesc)}`,
            );
          }
        }
      }

      // Handle debit/credit type changes - skip (empty)
      if (itemChanges.typeChanged) {
        const oldType = itemChanges.typeChanged.old || "";
        const newType = itemChanges.typeChanged.new || "";
        if (
          oldType !== newType &&
          (!shouldSkipDisplay(newType) || !shouldSkipDisplay(oldType))
        ) {
          if (!shouldSkipDisplay(newType)) {
            if (shouldSkipDisplay(oldType)) {
              modifications.push(`  Type: ${capitalizeForDisplay(newType)}`);
            } else {
              modifications.push(
                `  Type: ${capitalizeForDisplay(
                  oldType,
                )} → ${capitalizeForDisplay(newType)}`,
              );
            }
          } else if (!shouldSkipDisplay(oldType)) {
            modifications.push(`  Type: ${capitalizeForDisplay(oldType)}`);
          }
        }
      }

      // Only show if there are actual modifications
      if (modifications.length > 0) {
        changes.push(`Line Item ${lineNumber}:`);
        changes.push(...modifications);
        changes.push("");
      }
    }
  });

  // Format recurring journal info - ONLY if there are actual non-empty changes
  const recurringLines: string[] = [];

  if (
    recurringInfo.templateName &&
    recurringInfo.templateName.old !== recurringInfo.templateName.new
  ) {
    const oldVal = recurringInfo.templateName.old || "";
    const newVal = recurringInfo.templateName.new || "";
    if (!shouldSkipDisplay(newVal)) {
      if (shouldSkipDisplay(oldVal)) {
        recurringLines.push(`  Template Name: ${capitalizeForDisplay(newVal)}`);
      } else {
        recurringLines.push(
          `  Template Name: ${capitalizeForDisplay(
            oldVal,
          )} → ${capitalizeForDisplay(newVal)}`,
        );
      }
    } else if (!shouldSkipDisplay(oldVal)) {
      recurringLines.push(`  Template Name: ${capitalizeForDisplay(oldVal)}`);
    }
  }

  if (
    recurringInfo.frequency &&
    recurringInfo.frequency.old !== recurringInfo.frequency.new
  ) {
    const oldVal = formatFrequency(recurringInfo.frequency.old) || "";
    const newVal = formatFrequency(recurringInfo.frequency.new) || "";
    if (!shouldSkipDisplay(newVal)) {
      if (shouldSkipDisplay(oldVal)) {
        recurringLines.push(`  Frequency: ${capitalizeForDisplay(newVal)}`);
      } else {
        recurringLines.push(
          `  Frequency: ${capitalizeForDisplay(
            oldVal,
          )} → ${capitalizeForDisplay(newVal)}`,
        );
      }
    } else if (!shouldSkipDisplay(oldVal)) {
      recurringLines.push(`  Frequency: ${capitalizeForDisplay(oldVal)}`);
    }
  }

  if (
    recurringInfo.interval &&
    recurringInfo.interval.old !== recurringInfo.interval.new
  ) {
    const oldVal = recurringInfo.interval.old || "";
    const newVal = recurringInfo.interval.new || "";
    if (!shouldSkipDisplay(newVal)) {
      if (shouldSkipDisplay(oldVal)) {
        recurringLines.push(`  Interval: ${capitalizeForDisplay(newVal)}`);
      } else {
        recurringLines.push(
          `  Interval: ${capitalizeForDisplay(oldVal)} → ${capitalizeForDisplay(
            newVal,
          )}`,
        );
      }
    } else if (!shouldSkipDisplay(oldVal)) {
      recurringLines.push(`  Interval: ${capitalizeForDisplay(oldVal)}`);
    }
  }

  if (
    recurringInfo.startDate &&
    recurringInfo.startDate.old !== recurringInfo.startDate.new
  ) {
    const oldVal = convertDateFormat(recurringInfo.startDate.old || "") || "";
    const newVal = convertDateFormat(recurringInfo.startDate.new || "") || "";
    if (!shouldSkipDisplay(newVal)) {
      if (shouldSkipDisplay(oldVal)) {
        recurringLines.push(`  Starts On: ${newVal}`);
      } else {
        recurringLines.push(`  Starts On: ${oldVal} → ${newVal}`);
      }
    } else if (!shouldSkipDisplay(oldVal)) {
      recurringLines.push(`  Starts On: ${oldVal}`);
    }
  }

  // Never Expires / Ends On: show resulting state only (either "Never Expires: True" or "Ends On: <date>"), not "False → True"
  const neverExpiresNew = recurringInfo.neverExpires?.new
    ?.toString()
    .toLowerCase();
  const isNeverExpires = neverExpiresNew === "true" || neverExpiresNew === "1";
  const endDateNew = recurringInfo.endDate?.new
    ? convertDateFormat(recurringInfo.endDate.new) || ""
    : "";
  if (isNeverExpires) {
    recurringLines.push("  Never Expires: True");
  } else if (endDateNew && !shouldSkipDisplay(endDateNew)) {
    recurringLines.push(`  Ends On: ${endDateNew}`);
  } else if (
    recurringInfo.endDate &&
    recurringInfo.endDate.old !== recurringInfo.endDate.new
  ) {
    const oldVal = convertDateFormat(recurringInfo.endDate.old || "") || "";
    const newVal = convertDateFormat(recurringInfo.endDate.new || "") || "";
    if (!shouldSkipDisplay(newVal)) {
      recurringLines.push(`  Ends On: ${newVal}`);
    } else if (!shouldSkipDisplay(oldVal)) {
      recurringLines.push(`  Ends On: ${oldVal}`);
    }
  }

  // Only show "Recurring Journal:" section if we have at least one non-empty line
  if (recurringLines.length > 0) {
    if (changes.length > 0) changes.push("");
    changes.push("Recurring Journal:");
    changes.push(...recurringLines);
  }

  return changes;
};

/**
 * Format only recurring journal fields from raw_json for display (e.g. in ReviewStatusBanner when create_recurring only).
 * Returns lines like "Recurring Journal:", "  Template Name: X", "  Frequency: X", "  Starts On: X", "  Never Expires: True" or "  Ends On: X".
 */
export const formatRecurringJournalFieldsOnly = (rawJson: any): string[] => {
  if (!rawJson) return [];
  const j = rawJson.journal || rawJson;
  const lines: string[] = [];
  const formatFreq = (f: string) => {
    const m: Record<string, string> = {
      start_of_month: "Monthly (Start)",
      end_of_month: "Monthly (End)",
      daily: "Daily",
      weekly: "Weekly",
    };
    return (
      m[f] || (f ? f.charAt(0).toUpperCase() + f.slice(1).toLowerCase() : "")
    );
  };
  if (j.template_name) {
    lines.push("  Template Name: " + (j.template_name || ""));
  }
  if (j.recurrence_frequency) {
    lines.push("  Frequency: " + formatFreq(j.recurrence_frequency));
  }
  if (j.starts_on) {
    lines.push("  Starts On: " + convertDateFormat(String(j.starts_on)));
  }
  const neverExpires =
    j.never_expires === "1" ||
    j.never_expires === 1 ||
    j.never_expires === true;
  if (neverExpires) {
    lines.push("  Never Expires: True");
  } else if (j.ends_on) {
    lines.push("  Ends On: " + convertDateFormat(String(j.ends_on)));
  }
  if (lines.length === 0) return [];
  return ["Recurring Journal:", ...lines];
};

/**
 * Check if raw_json has recurring journal parameters (template_name, recurrence_frequency, etc.)
 */
export const hasRecurringParamsInRawJson = (rawJson: any): boolean => {
  if (!rawJson) return false;
  const j = rawJson.journal || rawJson;
  return !!(
    j.template_name ||
    j.recurrence_frequency ||
    j.starts_on ||
    j.ends_on ||
    j.never_expires === true ||
    j.never_expires === 1 ||
    j.never_expires === "1"
  );
};

/**
 * Format full journal from raw_json for draft-flow display (header + line items).
 * Used when we always show the updated journal (push_to_zoho, create_recurring from draft).
 * Excludes Notes per product requirement for journal approval/review display.
 */
export const formatJournalHeaderAndLineItemsFromRawJson = (
  rawJson: any,
): string[] => {
  if (!rawJson) return [];
  const j = rawJson.journal || rawJson;
  const lineItems = rawJson.line_items || j.line_items || [];
  const lines: string[] = [];

  lines.push("Journal:");
  if (j.date) {
    lines.push("  Date: " + convertDateFormat(String(j.date)));
  }
  if (j.reference_number != null && j.reference_number !== "") {
    lines.push("  Reference Number: " + String(j.reference_number));
  }
  const currencySymbol = j.currency_symbol || rawJson.currency_symbol || "₹";
  lines.push("  Currency: " + (currencySymbol || "₹"));
  const typeVal = j.type || rawJson.type || "";
  const method =
    typeVal === "both"
      ? "Both"
      : typeVal === "cash"
        ? "Cash"
        : typeVal === "accrual"
          ? "Accrual"
          : typeVal
            ? typeVal.charAt(0).toUpperCase() + typeVal.slice(1).toLowerCase()
            : "N/A";
  lines.push("  Method: " + method);

  let totalDebit = 0;
  let totalCredit = 0;
  lineItems.forEach((item: any) => {
    const amount = parseFloat(item.amount || 0);
    const isDebit =
      (item.is_debit_or_credit || "").toString().toLowerCase() === "debit";
    if (isDebit) totalDebit += amount;
    else totalCredit += amount;
  });
  lines.push(
    "  Total Debit: " +
      currencySymbol +
      " " +
      totalDebit.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  );
  lines.push(
    "  Total Credit: " +
      currencySymbol +
      " " +
      totalCredit.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
  );

  if (Array.isArray(lineItems) && lineItems.length > 0) {
    lines.push("");
    lines.push("Line Items:");
    lineItems.forEach((item: any, index: number) => {
      const lineNumber = index + 1;
      const accountName = item.account_name || "N/A";
      const contactName = item.contact_name || null;
      const amount = parseFloat(item.amount || 0);
      const isDebitOrCredit = (item.is_debit_or_credit || "")
        .toString()
        .toLowerCase();

      lines.push("  Line Item " + lineNumber + ":");
      lines.push("    Account: " + accountName);
      if (contactName) {
        lines.push("    Contact: " + contactName);
      }
      if (item.description != null && String(item.description).trim() !== "") {
        lines.push("    Description: " + String(item.description));
      }
      if (item.reference_display) {
        lines.push("    Invoice / Bill: " + item.reference_display);
      }
      if (isDebitOrCredit === "debit" && amount > 0) {
        lines.push(
          "    Debit: " +
            currencySymbol +
            " " +
            amount.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        );
      } else if (isDebitOrCredit === "credit" && amount > 0) {
        lines.push(
          "    Credit: " +
            currencySymbol +
            " " +
            amount.toLocaleString("en-IN", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
        );
      }
      // For non-INR (FBAC) line items: show exchange rate, amount in contact currency, and INR amount
      const rate = parseFloat(item.exchange_rate ?? 0);
      if (rate > 0 && amount > 0) {
        const contactSymbol = item.contact_currency_symbol || currencySymbol;
        const amountInContactCurrency = amount / rate;
        const fmt = (n: number) =>
          n.toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        lines.push("    Exchange Rate: " + fmt(rate));
        lines.push(
          "    Amount (" + contactSymbol + "): " + fmt(amountInContactCurrency),
        );
        lines.push("    Amount in INR (₹): " + fmt(amount));
      }
    });
  }

  return lines;
};

/**
 * Format field changes for Credit/Debit Notes
 * Exported for use in ReviewStatusBanner to ensure consistent formatting
 * For credit/debit notes, changes are primarily in line items (quantity and rate)
 * Field changes format: line_items[0].quantity, line_items[0].rate, etc.
 *
 * @param fieldChanges - Array of field changes from the review request
 */
export const formatCreditDebitNoteChanges = (
  fieldChanges: Array<{ field: string; old: string; new: string }>,
): string[] => {
  const changes: string[] = [];
  const idFieldsToHide = [
    "customer_id",
    "vendor_id",
    "account_id",
    "tax_id",
    "invoice_id",
    "bill_id",
    "_id",
    "id",
  ];

  // Group line item changes by index: { 0: { quantity: {...}, rate: {...} }, ... }
  const lineItemChanges: Record<
    number,
    {
      quantity?: { old: string; new: string };
      rate?: { old: string; new: string };
    }
  > = {};

  fieldChanges.forEach((fc) => {
    const fieldLower = fc.field.toLowerCase();

    // Skip changes where old and new values are the same
    const formattedOld = formatValueForDisplay(fc.old, fc.field);
    const formattedNew = formatValueForDisplay(fc.new, fc.field);
    if (formattedOld === formattedNew) {
      return;
    }

    // Skip ID fields
    if (
      idFieldsToHide.some((idField) => fieldLower.includes(idField)) ||
      fieldLower.endsWith("_id") ||
      fieldLower.endsWith("id")
    ) {
      return;
    }

    // Handle line item changes: line_items[0].quantity, line_items[0].rate, etc.
    const lineItemMatch = fc.field.match(
      /line_items\[(\d+)\]\.(quantity|rate)/i,
    );
    if (lineItemMatch) {
      const index = parseInt(lineItemMatch[1], 10);
      const field = lineItemMatch[2].toLowerCase() as "quantity" | "rate";

      if (!lineItemChanges[index]) {
        lineItemChanges[index] = {};
      }
      lineItemChanges[index][field] = {
        old: formatValueForDisplay(fc.old, fc.field),
        new: formatValueForDisplay(fc.new, fc.field),
      };
      return;
    }

    // For credit/debit notes, handle other fields (formatted field names)
    // Fields are already formatted by formatFieldName in tracking, but handle both cases
    if (!fc.field.startsWith("line_items")) {
      // Format field name if it has underscores (shouldn't happen with our tracking, but handle it)
      let displayField = fc.field;
      if (fc.field.includes("_") && !fc.field.match(/line_items\[\d+\]/)) {
        // Format snake_case to Title Case
        displayField = fc.field
          .split("_")
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ");
      }

      // Already formatted above, skip if same
      if (formattedOld !== formattedNew) {
        changes.push(`${displayField}: ${formattedOld} → ${formattedNew}`);
      }
    }
  });

  // Format grouped line item changes
  Object.keys(lineItemChanges)
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .forEach((indexStr) => {
      const index = parseInt(indexStr, 10);
      const itemChanges = lineItemChanges[index];
      const lineNumber = index + 1;

      if (itemChanges.quantity && itemChanges.rate) {
        // Only show if at least one value changed
        if (
          itemChanges.quantity.old !== itemChanges.quantity.new ||
          itemChanges.rate.old !== itemChanges.rate.new
        ) {
          changes.push(
            `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}, Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
          );
        }
      } else if (itemChanges.quantity) {
        if (itemChanges.quantity.old !== itemChanges.quantity.new) {
          changes.push(
            `Line Item ${lineNumber}: Quantity ${itemChanges.quantity.old} → ${itemChanges.quantity.new}`,
          );
        }
      } else if (itemChanges.rate) {
        if (itemChanges.rate.old !== itemChanges.rate.new) {
          changes.push(
            `Line Item ${lineNumber}: Rate ${itemChanges.rate.old} → ${itemChanges.rate.new}`,
          );
        }
      }
    });

  return changes;
};

/**
 * Helper to capitalize first letter of each word
 */
const capitalizeWords = (str: string): string => {
  if (!str) return str;
  return str
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Format customer details for display
 * Exported for use in ApprovalDetailPanel
 */
export const formatCustomerDetails = (
  rawJson: any,
  fieldChanges?: Array<{ field: string; old: string; new: string }>,
): string[] => {
  const changes: string[] = [];
  if (!rawJson) return changes;

  // Create a map of field changes for easy lookup
  const changesMap = new Map<string, { old: string; new: string }>();
  if (fieldChanges && fieldChanges.length > 0) {
    fieldChanges.forEach((fc) => {
      changesMap.set(fc.field.toLowerCase(), { old: fc.old, new: fc.new });
    });
  }

  // Helper to check if field changed and format accordingly
  const getFieldValue = (
    fieldName: string,
    currentValue: any,
    formatter?: (val: any) => string,
  ): string => {
    const change = changesMap.get(fieldName.toLowerCase());
    const formattedCurrent = formatter ? formatter(currentValue) : currentValue;

    if (change) {
      const oldVal =
        change.old && change.old.trim() !== ""
          ? formatter
            ? formatter(change.old)
            : change.old
          : "Empty";
      const newVal = formattedCurrent || "Empty";
      return `${oldVal} → ${newVal}`;
    }
    return formattedCurrent;
  };

  // Customer Information
  const customerInfo: string[] = [];
  if (rawJson.customer_name) {
    customerInfo.push(
      `  Name: ${getFieldValue(
        "customer_name",
        rawJson.customer_name,
        formatValue,
      )}`,
    );
  }
  if (rawJson.gst_treatment) {
    customerInfo.push(
      `  GST Treatment: ${getFieldValue(
        "gst_treatment",
        rawJson.gst_treatment,
        formatValue,
      )}`,
    );
  }
  if (rawJson.gstin_vat_tax_id && rawJson.gstin_vat_tax_id.trim()) {
    customerInfo.push(
      `  GSTIN: ${getFieldValue(
        "gstin_vat_tax_id",
        rawJson.gstin_vat_tax_id,
        (v) => v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.pan_tax_registration_no) {
    customerInfo.push(
      `  PAN: ${getFieldValue(
        "pan_tax_registration_no",
        rawJson.pan_tax_registration_no,
        (v) => v.toUpperCase(),
      )}`,
    );
  }
  if (customerInfo.length > 0) {
    changes.push("Customer Information:");
    changes.push(...customerInfo);
  }

  // Contact Information
  const contactInfo: string[] = [];
  if (rawJson.contact_name_1) {
    contactInfo.push(
      `  Primary Contact: ${getFieldValue(
        "contact_name_1",
        rawJson.contact_name_1,
        formatValue,
      )}`,
    );
  }
  if (rawJson.contact_number_1) {
    contactInfo.push(
      `  Phone: ${getFieldValue("contact_number_1", rawJson.contact_number_1)}`,
    );
  }
  if (rawJson.contact_email_1) {
    contactInfo.push(
      `  Email: ${getFieldValue(
        "contact_email_1",
        rawJson.contact_email_1,
        (v) => v.toLowerCase(),
      )}`,
    );
  }
  if (rawJson.contact_name_2) {
    contactInfo.push(
      `  Secondary Contact: ${getFieldValue(
        "contact_name_2",
        rawJson.contact_name_2,
        formatValue,
      )}`,
    );
  }
  if (rawJson.contact_number_2) {
    contactInfo.push(
      `  Secondary Phone: ${getFieldValue(
        "contact_number_2",
        rawJson.contact_number_2,
      )}`,
    );
  }
  if (rawJson.contact_email_2) {
    contactInfo.push(
      `  Secondary Email: ${getFieldValue(
        "contact_email_2",
        rawJson.contact_email_2,
        (v) => v.toLowerCase(),
      )}`,
    );
  }
  if (rawJson.communication_email_id) {
    contactInfo.push(
      `  Communication Email: ${getFieldValue(
        "communication_email_id",
        rawJson.communication_email_id,
        (v) => v.toLowerCase(),
      )}`,
    );
  }
  if (contactInfo.length > 0) {
    if (changes.length > 0) changes.push("");
    changes.push("Contact Information:");
    changes.push(...contactInfo);
  }

  // Address Information
  const addressInfo: string[] = [];
  if (rawJson.billing_address) {
    addressInfo.push(
      `  Billing: ${getFieldValue(
        "billing_address",
        rawJson.billing_address,
        formatValue,
      )}`,
    );
  }
  if (rawJson.shipping_address) {
    addressInfo.push(
      `  Shipping: ${getFieldValue(
        "shipping_address",
        rawJson.shipping_address,
        formatValue,
      )}`,
    );
  }
  if (rawJson.city) {
    addressInfo.push(
      `  City: ${getFieldValue("city", rawJson.city, formatValue)}`,
    );
  }
  if (rawJson.state) {
    addressInfo.push(
      `  State: ${getFieldValue("state", rawJson.state, (v) =>
        v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.country) {
    addressInfo.push(
      `  Country: ${getFieldValue("country", rawJson.country, (v) =>
        v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.postal_code) {
    addressInfo.push(
      `  Postal Code: ${getFieldValue("postal_code", rawJson.postal_code)}`,
    );
  }
  if (addressInfo.length > 0) {
    if (changes.length > 0) changes.push("");
    changes.push("Address:");
    changes.push(...addressInfo);
  }

  // Financial Information
  const financialInfo: string[] = [];
  if (rawJson.currency_name || rawJson.currency_code) {
    const currencyDisplay = rawJson.currency_name
      ? `${rawJson.currency_name}${
          rawJson.currency_code ? ` (${rawJson.currency_code})` : ""
        }`
      : rawJson.currency_code;
    financialInfo.push(
      `  Currency: ${getFieldValue("currency_id", currencyDisplay)}`,
    );
  }
  if (rawJson.payment_terms !== undefined && rawJson.payment_terms !== null) {
    financialInfo.push(
      `  Payment Terms: ${getFieldValue(
        "payment_terms",
        `${rawJson.payment_terms} days`,
      )}`,
    );
  }
  if (rawJson.credit_limit) {
    financialInfo.push(
      `  Credit Limit: ${getFieldValue("credit_limit", rawJson.credit_limit)}`,
    );
  }
  if (rawJson.bank_account_details) {
    financialInfo.push(
      `  Bank Account: ${getFieldValue(
        "bank_account_details",
        rawJson.bank_account_details,
      )}`,
    );
  }
  if (financialInfo.length > 0) {
    if (changes.length > 0) changes.push("");
    changes.push("Financial Information:");
    changes.push(...financialInfo);
  }

  return changes;
};

/**
 * Format vendor details for display
 * Exported for use in ApprovalDetailPanel
 */
export const formatVendorDetails = (
  rawJson: any,
  fieldChanges?: Array<{ field: string; old: string; new: string }>,
): string[] => {
  const changes: string[] = [];
  if (!rawJson) return changes;

  // Create a map of field changes for easy lookup
  const changesMap = new Map<string, { old: string; new: string }>();
  if (fieldChanges && fieldChanges.length > 0) {
    fieldChanges.forEach((fc) => {
      changesMap.set(fc.field.toLowerCase(), { old: fc.old, new: fc.new });
    });
  }

  // Helper to check if field changed and format accordingly
  const getFieldValue = (
    fieldName: string,
    currentValue: any,
    formatter?: (val: any) => string,
  ): string => {
    const change = changesMap.get(fieldName.toLowerCase());
    const formattedCurrent = formatter ? formatter(currentValue) : currentValue;

    if (change) {
      const oldVal =
        change.old && change.old.trim() !== ""
          ? formatter
            ? formatter(change.old)
            : change.old
          : "Empty";
      const newVal = formattedCurrent || "Empty";
      return `${oldVal} → ${newVal}`;
    }
    return formattedCurrent;
  };

  // Vendor Information
  changes.push("Vendor Information:");
  if (rawJson.vendor_legal_name) {
    changes.push(
      `  Legal Name: ${getFieldValue(
        "vendor_legal_name",
        rawJson.vendor_legal_name,
        formatValue,
      )}`,
    );
  }
  if (rawJson.vendor_legal_entity) {
    changes.push(
      `  Legal Entity: ${getFieldValue(
        "vendor_legal_entity",
        rawJson.vendor_legal_entity,
        formatValue,
      )}`,
    );
  }
  if (rawJson.trading_name) {
    changes.push(
      `  Trading Name: ${getFieldValue(
        "trading_name",
        rawJson.trading_name,
        formatValue,
      )}`,
    );
  }
  if (rawJson.type_of_vendor) {
    changes.push(
      `  Type: ${getFieldValue(
        "type_of_vendor",
        rawJson.type_of_vendor,
        formatValue,
      )}`,
    );
  }
  if (rawJson.business_category) {
    changes.push(
      `  Business Category: ${getFieldValue(
        "business_category",
        rawJson.business_category,
        formatValue,
      )}`,
    );
  }
  if (rawJson.vendor_classification) {
    changes.push(
      `  Classification: ${getFieldValue(
        "vendor_classification",
        rawJson.vendor_classification,
        formatValue,
      )}`,
    );
  }

  // Address Details
  changes.push("");
  changes.push("Address Details:");
  if (rawJson.registered_office_address) {
    changes.push(
      `  Registered Office: ${getFieldValue(
        "registered_office_address",
        rawJson.registered_office_address,
        formatValue,
      )}`,
    );
  }
  if (rawJson.correspondence_address) {
    changes.push(
      `  Correspondence: ${getFieldValue(
        "correspondence_address",
        rawJson.correspondence_address,
        formatValue,
      )}`,
    );
  }
  if (rawJson.city) {
    changes.push(`  City: ${getFieldValue("city", rawJson.city, formatValue)}`);
  }
  if (rawJson.state) {
    changes.push(
      `  State: ${getFieldValue("state", rawJson.state, (v) =>
        v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.country) {
    changes.push(
      `  Country: ${getFieldValue("country", rawJson.country, (v) =>
        v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.postal_code) {
    changes.push(
      `  Postal Code: ${getFieldValue("postal_code", rawJson.postal_code)}`,
    );
  }

  // Contact Information
  changes.push("");
  changes.push("Contact Information:");
  if (rawJson.primary_contact_name) {
    changes.push(
      `  Primary Contact: ${getFieldValue(
        "primary_contact_name",
        rawJson.primary_contact_name,
        formatValue,
      )}`,
    );
  }
  if (rawJson.primary_contact_phone) {
    changes.push(
      `  Phone: ${getFieldValue(
        "primary_contact_phone",
        rawJson.primary_contact_phone,
      )}`,
    );
  }
  if (rawJson.primary_contact_email) {
    changes.push(
      `  Email: ${getFieldValue(
        "primary_contact_email",
        rawJson.primary_contact_email,
        (v) => v.toLowerCase(),
      )}`,
    );
  }
  if (rawJson.alternate_contact) {
    changes.push(
      `  Alternate Contact: ${getFieldValue(
        "alternate_contact",
        rawJson.alternate_contact,
        formatValue,
      )}`,
    );
  }
  if (rawJson.escalation_contact) {
    changes.push(
      `  Escalation Contact: ${getFieldValue(
        "escalation_contact",
        rawJson.escalation_contact,
        formatValue,
      )}`,
    );
  }
  if (rawJson.website) {
    changes.push(
      `  Website: ${getFieldValue("website", rawJson.website, (v) =>
        v.toLowerCase(),
      )}`,
    );
  }

  // Compliance & Registration
  changes.push("");
  changes.push("Compliance & Registration:");
  if (rawJson.pan_tax_id) {
    changes.push(`  PAN: ${rawJson.pan_tax_id.toUpperCase()}`);
  }
  if (rawJson.gstin) {
    changes.push(`  GSTIN: ${rawJson.gstin.toUpperCase()}`);
  }
  if (rawJson.msme_flag) {
    changes.push(
      `  MSME: ${
        rawJson.msme_flag === "Y"
          ? "Yes"
          : rawJson.msme_flag === "N"
            ? "No"
            : formatValue(rawJson.msme_flag)
      }`,
    );
  }
  if (rawJson.company_registration_number) {
    changes.push(
      `  Company Registration: ${rawJson.company_registration_number.toUpperCase()}`,
    );
  }
  if (rawJson.import_export_code) {
    changes.push(
      `  Import/Export Code: ${rawJson.import_export_code.toUpperCase()}`,
    );
  }
  if (rawJson.udyam_number) {
    changes.push(`  Udyam Number: ${rawJson.udyam_number.toUpperCase()}`);
  }

  // Banking Details
  changes.push("");
  changes.push("Banking Details:");
  if (rawJson.beneficiary_name) {
    changes.push(
      `  Beneficiary: ${getFieldValue(
        "beneficiary_name",
        rawJson.beneficiary_name,
        formatValue,
      )}`,
    );
  }
  if (rawJson.bank_account_number) {
    changes.push(
      `  Account Number: ${getFieldValue(
        "bank_account_number",
        rawJson.bank_account_number,
      )}`,
    );
  }
  if (rawJson.bank_name) {
    changes.push(
      `  Bank: ${getFieldValue("bank_name", rawJson.bank_name, formatValue)}`,
    );
  }
  if (rawJson.branch) {
    changes.push(
      `  Branch: ${getFieldValue("branch", rawJson.branch, formatValue)}`,
    );
  }
  if (rawJson.ifsc_swift_code) {
    changes.push(
      `  IFSC/SWIFT: ${getFieldValue(
        "ifsc_swift_code",
        rawJson.ifsc_swift_code,
        (v) => v.toUpperCase(),
      )}`,
    );
  }
  if (rawJson.beneficiary_address) {
    changes.push(
      `  Beneficiary Address: ${getFieldValue(
        "beneficiary_address",
        rawJson.beneficiary_address,
        formatValue,
      )}`,
    );
  }

  // Payment & Financial Terms
  changes.push("");
  changes.push("Payment & Financial Terms:");
  if (rawJson.currency_name || rawJson.currency_code) {
    const currencyDisplay = rawJson.currency_name
      ? `${rawJson.currency_name}${
          rawJson.currency_code ? ` (${rawJson.currency_code})` : ""
        }`
      : rawJson.currency_code;
    changes.push(`  Currency: ${currencyDisplay}`);
  }
  if (rawJson.payment_terms !== undefined && rawJson.payment_terms !== null) {
    changes.push(`  Payment Terms: ${rawJson.payment_terms} days`);
  }
  if (rawJson.preferred_payment_method) {
    changes.push(
      `  Payment Method: ${formatValue(rawJson.preferred_payment_method)}`,
    );
  }
  if (rawJson.credit_limit) {
    changes.push(`  Credit Limit: ${rawJson.credit_limit}`);
  }
  if (rawJson.advance_security_deposit) {
    changes.push(`  Security Deposit: ${rawJson.advance_security_deposit}`);
  }

  // Taxation
  changes.push("");
  changes.push("Taxation:");
  if (rawJson.gst_category) {
    changes.push(`  GST Category: ${formatValue(rawJson.gst_category)}`);
  }
  if (rawJson.gst_treatment) {
    changes.push(`  GST Treatment: ${formatValue(rawJson.gst_treatment)}`);
  }
  if (rawJson.tds_applicable) {
    changes.push(
      `  TDS Applicable: ${
        rawJson.tds_applicable === "Y"
          ? "Yes"
          : rawJson.tds_applicable === "N"
            ? "No"
            : rawJson.tds_applicable
      }`,
    );
  }
  if (rawJson.tds_name) {
    changes.push(`  TDS Tax Rate: ${rawJson.tds_name}`);
  }
  if (rawJson.party_type) {
    changes.push(`  Party Type: ${formatValue(rawJson.party_type)}`);
  }

  // Other Details
  if (rawJson.risk_rating) {
    changes.push("");
    changes.push(`Risk Rating: ${formatValue(rawJson.risk_rating)}`);
  }
  if (rawJson.invoice_submission_method) {
    changes.push(
      `Invoice Submission: ${getFieldValue(
        "invoice_submission_method",
        rawJson.invoice_submission_method,
        formatValue,
      )}`,
    );
  }

  return changes;
};

interface ReviewRequestModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  moduleCode: string;
  actionCode: string;
  entityType: string;
  entityId: number;
  documentNumber?: string; // Invoice number, bill number, etc.
  changeDetails?: ChangeDetails;
  onSuccess?: (comments?: string) => void; // Pass comments to onSuccess callback
  onError?: (error: Error) => void;
  disableSubmit?: boolean; // Disable submit button (e.g., when no changes detected)
  transactionData?: Transaction | null; // For bank_transaction entity type
  skipNavigation?: boolean; // If true, don't navigate back after submission
  skipReviewRequest?: boolean; // If true, don't send review request, just call onSuccess with comments
  title?: string; // Optional custom title (e.g., "Reject this Invoice", "Mark this Bill as Duplicate")
}

export const ReviewRequestModal: React.FC<ReviewRequestModalProps> = ({
  open,
  onOpenChange,
  moduleCode,
  actionCode,
  entityType,
  entityId,
  documentNumber,
  changeDetails,
  onSuccess,
  onError,
  disableSubmit = false,
  transactionData,
  skipNavigation = false,
  skipReviewRequest = false,
  title,
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError: showToastError } = useToast();
  const { taxes } = useTaxes();
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const successShownRef = useRef(false);

  useEffect(() => {
    if (open) successShownRef.current = false;
  }, [open]);

  const handleClose = () => {
    if (!isSubmitting) {
      setComments("");
      setError(null);
      onOpenChange(false);
    }
  };

  const handleSubmit = async () => {
    if (!comments.trim()) {
      setError("Comment is required");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // If skipReviewRequest is true, just call onSuccess with comments and return
      if (skipReviewRequest) {
        // Call onSuccess callback with comments first (parent will handle creating entity and review request)
        onSuccess?.(comments.trim());
        // Close modal after onSuccess
        handleClose();
        return;
      }

      // For bank_transaction entities, ensure transaction_date, description, and transaction_amount are included
      let enhancedChangeDetails = changeDetails;
      if (entityType === "bank_transaction" && transactionData) {
        enhancedChangeDetails = {
          ...changeDetails,
          raw_json: {
            ...(changeDetails?.raw_json || {}),
            transaction_date: transactionData.date,
            description: transactionData.description,
            transaction_amount: transactionData.amount,
          },
        };
      }

      const requestData: CreateReviewRequestDTO = {
        module_code: moduleCode,
        action_code: actionCode,
        entity_type: entityType,
        entity_id: entityId,
        request_comments: comments.trim(),
        change_details: enhancedChangeDetails,
      };
      console.log("Review Request data", requestData);
      await createReviewRequest(requestData);
      if (!successShownRef.current) {
        successShownRef.current = true;
        showSuccess("Review request submitted successfully");
      }

      // Call onSuccess callback (parent handles navigation / state cleanup; do not show same success toast again)
      onSuccess?.(comments.trim());

      // Close modal after onSuccess
      handleClose();

      // Navigate back only if skipNavigation is false
      if (!skipNavigation) {
        // Use setTimeout to ensure modal closes before navigation
        setTimeout(() => {
          navigate(-1);
        }, 100);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to create review request";
      setError(errorMessage);
      showToastError(errorMessage);
      onError?.(err instanceof Error ? err : new Error(errorMessage));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // SHARED HELPER FUNCTIONS
  // ============================================================================

  // Use the exported formatValue function (defined above)
  // formatValue is now exported at the top of the file
  // formatCreditDebitNoteChanges is also exported at the top of the file

  // ============================================================================
  // GL OVERRIDE FORMATTER - For invoices/bills with override_gl_permanent
  // ============================================================================

  /**
   * Format invoice/bill changes with GL override separation
   * Only used when actionCode is "override_gl_permanent" for invoices/bills
   * Handles two scenarios:
   * 1. Only GL overrides in field_changes (other actions don't need review) → show only GL overrides
   * 2. GL overrides + other changes in field_changes (other actions also need review) → show both sections
   */
  const formatInvoiceBillChangesWithGLOverride = (
    fieldChanges: Array<{ field: string; old: string; new: string }>,
    changeDetails?: any,
  ): string | null => {
    const glOverrideChanges: Array<{
      lineItemIndex: number;
      old: string;
      new: string;
    }> = [];
    const otherFieldChanges: Array<{
      field: string;
      old: string;
      new: string;
    }> = [];

    // Get all line item indices that have GL overrides from line_item_gl_data array
    const glOverrideLineItemIndices = new Set<number>();

    // Check if line_item_gl_data is an array (new format supporting multiple GL overrides)
    if (Array.isArray(changeDetails?.line_item_gl_data)) {
      changeDetails.line_item_gl_data.forEach((glData: any) => {
        if (glData.line_item_index !== undefined) {
          glOverrideLineItemIndices.add(glData.line_item_index);
        }
      });
    }
    // Also check for backward compatibility (single GL override object)
    else if (
      changeDetails?.line_item_gl_data &&
      changeDetails.line_item_gl_data.old_account_name &&
      changeDetails.line_item_gl_data.new_account_name
    ) {
      const lineItemIndex =
        changeDetails.line_item_index !== undefined
          ? changeDetails.line_item_index
          : 0;
      glOverrideLineItemIndices.add(lineItemIndex);
    }

    // Separate GL override changes from other changes
    fieldChanges.forEach((fc) => {
      // Check if this is a GL override change (Line Item X: Account Name)
      // Pattern: "Line Item 1: Account Name", "Line Item 2: Account Name", etc.
      const glOverrideMatch = fc.field.match(
        /^Line Item (\d+): Account Name$/i,
      );
      if (glOverrideMatch) {
        // Extract line item index (convert from 1-based to 0-based for consistency)
        const lineItemNumber = parseInt(glOverrideMatch[1], 10);
        const lineItemIndex = lineItemNumber - 1; // Convert to 0-based index

        // Check if this line item has a GL override (from line_item_gl_data array)
        // If action_types includes override_gl_permanent or override_gl_once, treat all "Line Item X: Account Name" as GL overrides
        const hasGLOverrideAction =
          changeDetails?.action_types?.includes("override_gl_permanent") ||
          changeDetails?.action_types?.includes("override_gl_once");

        const isGLOverride =
          hasGLOverrideAction &&
          (glOverrideLineItemIndices.has(lineItemIndex) ||
            // If no line_item_gl_data but action_types has GL override, treat all as GL overrides
            (glOverrideLineItemIndices.size === 0 && hasGLOverrideAction));

        if (isGLOverride) {
          // Check if this GL override is not already in glOverrideChanges (prevent duplicates)
          const alreadyAdded = glOverrideChanges.some(
            (gl) => gl.lineItemIndex === lineItemIndex,
          );

          if (!alreadyAdded) {
            glOverrideChanges.push({
              lineItemIndex,
              old: fc.old,
              new: fc.new,
            });
          }
        } else {
          // This is a regular account change (scope "once"), not a GL override
          otherFieldChanges.push(fc);
        }
      } else {
        // This is a regular change (not a GL override)
        otherFieldChanges.push(fc);
      }
    });

    // Also add GL overrides from line_item_gl_data array if not already in field_changes
    if (Array.isArray(changeDetails?.line_item_gl_data)) {
      changeDetails.line_item_gl_data.forEach((glData: any) => {
        if (
          glData.line_item_index !== undefined &&
          glData.old_account_name &&
          glData.new_account_name
        ) {
          // Check if this GL override is not already in glOverrideChanges
          const alreadyAdded = glOverrideChanges.some(
            (gl) => gl.lineItemIndex === glData.line_item_index,
          );

          if (!alreadyAdded) {
            glOverrideChanges.push({
              lineItemIndex: glData.line_item_index,
              old: glData.old_account_name,
              new: glData.new_account_name,
            });
          }
        }
      });
    }

    // Build the formatted output
    const output: string[] = [];

    // Add GL Override section if there are GL overrides
    if (glOverrideChanges.length > 0) {
      // Sort by line item index to show in order
      glOverrideChanges
        .sort((a, b) => a.lineItemIndex - b.lineItemIndex)
        .forEach((gl) => {
          const formattedOld = formatValue(gl.old);
          const formattedNew = formatValue(gl.new);
          // Display line item number (1-based for user display)
          const lineItemNumber = gl.lineItemIndex + 1;
          output.push(`GL Override request for Line Item ${lineItemNumber}`);
          output.push(`Account: ${formattedOld} → ${formattedNew}`);
        });
    }

    const kind = moduleCode?.toLowerCase() === "invoice" ? "invoice" : "bill";
    const { systemChanges } = formatInvoiceBillReviewSummarySections(
      kind,
      changeDetails,
      taxes,
    );
    if (systemChanges.length > 0) {
      if (output.length > 0) {
        output.push("");
      }
      output.push("System-applied updates:");
      output.push(...systemChanges);
    }

    // Add Other Changes section ONLY if there are other changes
    // This indicates that other actions (update/approve/update_after_push) also need review
    if (otherFieldChanges.length > 0) {
      const otherChanges = formatInvoiceBillFieldChangesForApprovalList(
        kind,
        otherFieldChanges,
      );
      if (otherChanges.length > 0) {
        if (output.length > 0) {
          output.push(""); // Add blank line separator
        }
        output.push("Other changes:");
        output.push(...otherChanges);
      }
    }

    return output.length > 0 ? output.join("\n") : null;
  };

  const actionLabels = REVIEW_ACTION_LABELS;

  /**
   * Format action types into readable text
   * Example: ["override_gl_permanent", "update_after_push"] => "Permanent GL Override + Update After Pushed to Zoho"
   * Post-close change (month_close) is always shown first when present.
   */
  const formatActionTypes = (actionTypes?: string[]): string | null => {
    if (!actionTypes || actionTypes.length === 0) return null;

    const sorted = sortActionTypesForDisplay(actionTypes);
    const formattedActions = sorted
      .map((action) => actionLabels[action] || action)
      .join(" + ");

    return formattedActions;
  };

  /**
   * Format field changes for Bank Transactions
   * Shows category type, customer/vendor name, invoices/bills details, advance, and split info
   */
  const formatBankTransactionChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const rawJson = changeDetails.raw_json;
    const categoryType = rawJson.category_type || "";

    if (Array.isArray(rawJson.split_items)) {
      const currencySymbol = rawJson.currency_symbol || "₹";
      const formatCurrencyAmount = (amount: unknown): string => {
        const numericAmount = Number(amount);
        if (!Number.isFinite(numericAmount)) return "N/A";

        return `${currencySymbol}${numericAmount.toLocaleString("en-IN", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      };

      changes.push(
        `Action: ${actionLabels.split_transaction_amount}`,
        `Original Transaction: ${rawJson.description || "N/A"}`,
        `Original Amount: ${formatCurrencyAmount(rawJson.transaction_amount)}`,
        `Split Items: ${rawJson.split_items.length}`,
      );

      rawJson.split_items.forEach((item: any, index: number) => {
        changes.push(
          `  Item ${index + 1}: ${item.description || "N/A"} - ${formatCurrencyAmount(item.amount)}`,
        );
      });

      return changes;
    }

    // Show Category Type at the top
    if (categoryType) {
      changes.push(`Category Type: ${formatValue(categoryType)}`);
    }

    // Handle customer_payment
    if (categoryType === "customer_payment") {
      // Handle multiple customers (split) - check this first
      if (rawJson.customers && Array.isArray(rawJson.customers)) {
        if (rawJson.customers.length > 1) {
          changes.push(
            `Split Transaction: ${rawJson.customers.length} customers`,
          );
        }

        rawJson.customers.forEach((customer: any, index: number) => {
          changes.push("");
          changes.push(
            `Customer ${index + 1}: ${customer.customer_name || "N/A"}`,
          );

          // Show advance for this customer
          if (customer.advance_payment && customer.advance_payment > 0) {
            changes.push(`  Advance: ${customer.advance_payment}`);
          }

          // Show invoices for this customer
          if (customer.invoices && customer.invoices.length > 0) {
            changes.push("  Invoices:");
            customer.invoices.forEach((invoice: any) => {
              const invoiceDetails = [
                `    Invoice #${invoice.invoice_number || invoice.invoice_id}`,
                `      Total: ${invoice.total || "N/A"}`,
                `      Balance: ${invoice.balance || "N/A"}`,
                `      Amount Applied: ${invoice.amount_applied || "N/A"}`,
              ];

              // Show TDS if applied
              if (invoice.is_tds_applied === 1) {
                const tdsName = invoice.tds_tax_name || "N/A";
                const tdsPercentage = invoice.tds_tax_percentage
                  ? `${invoice.tds_tax_percentage}%`
                  : "";
                const tdsAmount = invoice.tds_tax_amount || "N/A";
                invoiceDetails.push(
                  `      TDS: ${tdsName}${
                    tdsPercentage ? ` (${tdsPercentage})` : ""
                  } - ${tdsAmount}`,
                );
              } else if (invoice.tds_tax_amount && invoice.tds_tax_amount > 0) {
                const tdsName = invoice.tds_tax_name || "N/A";
                const tdsPercentage = invoice.tds_tax_percentage
                  ? `${invoice.tds_tax_percentage}%`
                  : "";
                const tdsAmount = invoice.tds_tax_amount || "N/A";
                invoiceDetails.push(
                  `      TDS: ${tdsName}${
                    tdsPercentage ? ` (${tdsPercentage})` : ""
                  } - ${tdsAmount}`,
                );
              }

              changes.push(...invoiceDetails);
            });
          }
        });
      } else if (rawJson.categorize_in_zoho_json) {
        // Handle single customer payment
        // Show customer name if available
        if (rawJson.customer_name) {
          changes.push(`Customer: ${rawJson.customer_name}`);
        }

        const categorizeData = rawJson.categorize_in_zoho_json;
        const invoices = categorizeData.invoices || [];

        // Show advance payment specially
        if (rawJson.advance_payment && rawJson.advance_payment > 0) {
          changes.push(`Advance of ${rawJson.advance_payment} applied`);
        }

        // Show invoices
        if (invoices.length > 0) {
          changes.push("");
          changes.push("Invoices:");
          invoices.forEach((invoice: any) => {
            const invoiceDetails = [
              `  Invoice #${invoice.invoice_number || invoice.invoice_id}`,
              `    Total: ${invoice.total || "N/A"}`,
              `    Balance: ${invoice.balance || "N/A"}`,
              `    Amount Applied: ${invoice.amount_applied || "N/A"}`,
            ];

            // Show TDS if applied
            if (invoice.is_tds_applied === 1) {
              const tdsName = invoice.tds_tax_name || "N/A";
              const tdsPercentage = invoice.tds_tax_percentage
                ? `${invoice.tds_tax_percentage}%`
                : "";
              const tdsAmount = invoice.tds_tax_amount || "N/A";
              invoiceDetails.push(
                `    TDS: ${tdsName}${
                  tdsPercentage ? ` (${tdsPercentage})` : ""
                } - ${tdsAmount}`,
              );
            } else if (invoice.tds_tax_amount && invoice.tds_tax_amount > 0) {
              const tdsName = invoice.tds_tax_name || "N/A";
              const tdsPercentage = invoice.tds_tax_percentage
                ? `${invoice.tds_tax_percentage}%`
                : "";
              const tdsAmount = invoice.tds_tax_amount || "N/A";
              invoiceDetails.push(
                `    TDS: ${tdsName}${
                  tdsPercentage ? ` (${tdsPercentage})` : ""
                } - ${tdsAmount}`,
              );
            }

            changes.push(...invoiceDetails);
          });
        }
      }
    }

    // Handle vendor_payment (similar structure but for vendors and bills)
    if (categoryType === "vendor_payment") {
      // Show vendor name if available
      if (rawJson.vendor_name) {
        changes.push(`Vendor: ${rawJson.vendor_name}`);
      }

      // Handle vendor payment with bills
      if (rawJson.categorize_in_zoho_json) {
        const categorizeData = rawJson.categorize_in_zoho_json;
        const bills = categorizeData.bills || [];

        // Show advance payment specially
        if (rawJson.advance_payment && rawJson.advance_payment > 0) {
          changes.push(`Advance of ${rawJson.advance_payment} applied`);
        }

        // Show bills
        if (bills.length > 0) {
          changes.push("");
          changes.push("Bills:");
          bills.forEach((bill: any) => {
            const billDetails = [
              `  Bill #${bill.bill_number || bill.bill_id}`,
              `    Total: ${bill.total || "N/A"}`,
              `    Balance: ${bill.balance || "N/A"}`,
              `    Amount Applied: ${bill.amount_applied || "N/A"}`,
            ];

            // Show TDS if applied
            if (bill.is_tds_applied === 1) {
              const tdsName = bill.tds_tax_name || "N/A";
              const tdsPercentage = bill.tds_tax_percentage
                ? `${bill.tds_tax_percentage}%`
                : "";
              const tdsAmount = bill.tds_tax_amount || "N/A";
              billDetails.push(
                `    TDS: ${tdsName}${
                  tdsPercentage ? ` (${tdsPercentage})` : ""
                } - ${tdsAmount}`,
              );
            } else if (bill.tds_tax_amount && bill.tds_tax_amount > 0) {
              const tdsName = bill.tds_tax_name || "N/A";
              const tdsPercentage = bill.tds_tax_percentage
                ? `${bill.tds_tax_percentage}%`
                : "";
              const tdsAmount = bill.tds_tax_amount || "N/A";
              billDetails.push(
                `    TDS: ${tdsName}${
                  tdsPercentage ? ` (${tdsPercentage})` : ""
                } - ${tdsAmount}`,
              );
            }

            changes.push(...billDetails);
          });
        }
      }

      // Handle multiple vendors (split)
      if (rawJson.vendors && Array.isArray(rawJson.vendors)) {
        if (rawJson.vendors.length > 1) {
          changes.push("");
          changes.push(`Split Transaction: ${rawJson.vendors.length} vendors`);
        }

        rawJson.vendors.forEach((vendor: any, index: number) => {
          changes.push("");
          changes.push(`Vendor ${index + 1}: ${vendor.vendor_name || "N/A"}`);

          // Show advance for this vendor
          if (vendor.advance_payment && vendor.advance_payment > 0) {
            changes.push(`  Advance: ${vendor.advance_payment}`);
          }

          // Show bills for this vendor
          if (vendor.bills && vendor.bills.length > 0) {
            changes.push("  Bills:");
            vendor.bills.forEach((bill: any) => {
              const billDetails = [
                `    Bill #${bill.bill_number || bill.bill_id}`,
                `      Total: ${bill.total || "N/A"}`,
                `      Balance: ${bill.balance || "N/A"}`,
                `      Amount Applied: ${bill.amount_applied || "N/A"}`,
              ];

              // Show TDS if applied
              if (bill.is_tds_applied === 1) {
                const tdsName = bill.tds_tax_name || "N/A";
                const tdsPercentage = bill.tds_tax_percentage
                  ? `${bill.tds_tax_percentage}%`
                  : "";
                const tdsAmount = bill.tds_tax_amount || "N/A";
                billDetails.push(
                  `      TDS: ${tdsName}${
                    tdsPercentage ? ` (${tdsPercentage})` : ""
                  } - ${tdsAmount}`,
                );
              } else if (bill.tds_tax_amount && bill.tds_tax_amount > 0) {
                const tdsName = bill.tds_tax_name || "N/A";
                const tdsPercentage = bill.tds_tax_percentage
                  ? `${bill.tds_tax_percentage}%`
                  : "";
                const tdsAmount = bill.tds_tax_amount || "N/A";
                billDetails.push(
                  `      TDS: ${tdsName}${
                    tdsPercentage ? ` (${tdsPercentage})` : ""
                  } - ${tdsAmount}`,
                );
              }

              changes.push(...billDetails);
            });
          }
        });
      }
    }

    return changes;
  };

  /**
   * Format field changes for Journal Entries (internal wrapper)
   * Uses the exported formatJournalChangesForBanner function
   */
  const formatJournalChanges = (
    fieldChanges: Array<{ field: string; old: string; new: string }>,
    rawJson?: any,
  ): string[] => {
    // Use the exported function, pass rawJson and originalJson for context
    const originalJson = changeDetails?.original_json;
    return formatJournalChangesForBanner(fieldChanges, rawJson, originalJson);
  };

  /**
   * Format change details for Month End Checklist Items
   * Shows title, description, accounting area, attachments, and notes
   */
  const formatMonthEndChecklistChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const rawJson = changeDetails.raw_json;

    // Title
    if (rawJson.item_title) {
      changes.push(`Title: ${rawJson.item_title}`);
    }

    // Description
    if (rawJson.item_description) {
      changes.push(`Description: ${rawJson.item_description}`);
    }

    // Accounting Area
    if (rawJson.accounting_area) {
      changes.push(`Accounting Area: ${rawJson.accounting_area}`);
    }

    // Module (if available)
    if (rawJson.module) {
      changes.push(`Module: ${rawJson.module}`);
    }

    // Attachments
    if (
      rawJson.attachments &&
      Array.isArray(rawJson.attachments) &&
      rawJson.attachments.length > 0
    ) {
      changes.push(
        `Attachments: ${rawJson.attachments.length} file${
          rawJson.attachments.length > 1 ? "s" : ""
        } uploaded`,
      );
      rawJson.attachments.forEach((att: any, index: number) => {
        const fileName =
          att.original_filename || att.file_name || `Attachment ${index + 1}`;
        changes.push(`  • ${fileName}`);
      });
    }

    // Notes (if provided)
    if (rawJson.notes) {
      changes.push(`Notes: ${rawJson.notes}`);
    }

    // System Info (if available)
    if (rawJson.system_info) {
      if (Array.isArray(rawJson.system_info)) {
        rawJson.system_info.forEach((info: string) => {
          changes.push(`System Info: ${info}`);
        });
      } else if (typeof rawJson.system_info === "object") {
        Object.entries(rawJson.system_info).forEach(([key, value]) => {
          changes.push(`System Info (${key}): ${String(value)}`);
        });
      } else {
        changes.push(`System Info: ${String(rawJson.system_info)}`);
      }
    }

    return changes;
  };

  /**
   * Format change details for Bank Account / Credit Card creation
   * Shows account type, name, number, and currency in a clear summary for reviewers
   */
  const formatBankAccountChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const rawJson = changeDetails.raw_json;
    const ac = (actionCode || "create").toLowerCase();
    const isCreditType =
      rawJson.account_type === "credit" ||
      rawJson.account_type === "credit_card";
    const accountType = isCreditType ? "Credit card" : "Bank account";

    if (ac === "delete") {
      changes.push(`Action: Delete ${accountType}`);
      if (rawJson.account_name) {
        changes.push(`Account name: ${String(rawJson.account_name)}`);
      }
      if (rawJson.account_number != null && rawJson.account_number !== "") {
        changes.push(`Account number: ${String(rawJson.account_number)}`);
      }
      changes.push("");
      changes.push(
        "⚠ This account will be permanently removed from Zoho Books.",
      );
      return changes;
    }

    if (ac === "update") {
      changes.push("Action: Update account");
    }

    const currencyDisplay =
      [rawJson.currency_code, rawJson.currency_name]
        .filter(Boolean)
        .join(" – ") ||
      rawJson.currency_id ||
      "—";

    changes.push(`Account type: ${accountType}`);
    if (rawJson.account_name) {
      changes.push(`Account name: ${String(rawJson.account_name)}`);
    }
    if (rawJson.account_number != null && rawJson.account_number !== "") {
      changes.push(`Account number: ${String(rawJson.account_number)}`);
    }
    changes.push(`Currency: ${currencyDisplay}`);

    const ob = rawJson.opening_balance;
    const obDate = rawJson.opening_balance_date;
    if (ob != null && ob !== "" && !Number.isNaN(Number(ob))) {
      changes.push(
        `Opening balance: ${Number(ob).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
      );
    }
    if (obDate != null && String(obDate).trim() !== "") {
      changes.push(
        `Opening balance date: ${convertDateFormat(String(obDate).trim())}`,
      );
    }

    // For update requests, show field-level diffs when original_json is available
    if (ac === "update" && changeDetails?.original_json) {
      const original = changeDetails.original_json as Record<string, unknown>;
      const next = rawJson as Record<string, unknown>;
      const diffs: Array<{ label: string; oldVal: string; newVal: string }> =
        [];

      const pick = (v: unknown) => (v == null ? "—" : String(v));
      const fmtType = (v: unknown) => {
        const s = String(v ?? "");
        if (s === "credit" || s === "credit_card") return "Credit card";
        if (s === "bank") return "Bank account";
        return s || "—";
      };

      const cmp = (
        key: string,
        label: string,
        fmt: (v: unknown) => string = pick,
      ) => {
        if (!Object.prototype.hasOwnProperty.call(next, key)) return;
        const o = fmt((original as any)[key]);
        const n = fmt((next as any)[key]);
        if (o !== n) diffs.push({ label, oldVal: o, newVal: n });
      };

      changes.push("");
      changes.push("Changes Requested:");
      cmp("account_name", "Account name");
      cmp("account_type", "Account type", fmtType);
      cmp("account_number", "Account number");
      cmp("currency_code", "Currency");
      // opening balance only when explicitly present in update payload
      cmp("opening_balance", "Opening balance", (v) =>
        v == null || v === ""
          ? "Empty"
          : Number(v).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
      );
      cmp("opening_balance_date", "Opening balance date", (v) =>
        v ? convertDateFormat(String(v).trim()) : "Empty",
      );

      if (diffs.length === 0) {
        changes.push("  (No field changes detected)");
      } else {
        diffs.forEach((d) =>
          changes.push(`  ${d.label}: ${d.oldVal} → ${d.newVal}`),
        );
      }
    }

    return changes;
  };

  /**
   * Format change details for GL (General Ledger / chart-of-account) actions.
   * Handles create, edit (with field_changes), and delete.
   */
  const formatGlChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const raw = changeDetails.raw_json as Record<string, unknown>;
    const actionTypes = changeDetails.action_types || [];

    if ((actionCode || "").toLowerCase() === "bulk_upload") {
      const rows = (raw as { rows?: unknown }).rows;
      const n = Array.isArray(rows) ? rows.length : 0;
      if (n > 0) {
        changes.push(
          `Bulk upload: ${n} row(s) will be created here after your manager approves.`,
        );
      } else {
        changes.push("Bulk upload pending review.");
      }
      return changes;
    }

    const fmtType = (val: string) =>
      String(val)
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");

    if (actionTypes.includes("delete")) {
      changes.push(`Account Name: ${String(raw.account_name ?? "—")}`);
      if (raw.account_type)
        changes.push(`Account Type: ${fmtType(String(raw.account_type))}`);
      changes.push("");
      changes.push("⚠ This GL account will be permanently deleted.");
      return changes;
    }

    // create or edit — always show the current state from raw_json
    if (raw.account_name)
      changes.push(`Account Name: ${String(raw.account_name)}`);
    if (raw.account_type)
      changes.push(`Account Type: ${fmtType(String(raw.account_type))}`);
    if (raw.parent_account_name) {
      changes.push(`Parent Account: ${String(raw.parent_account_name)}`);
    } else if (raw.parent_account_id) {
      changes.push(`Parent Account: ${String(raw.parent_account_id)}`);
    }

    const usedIn: string[] = [];
    if (raw.invoice) usedIn.push("Invoices");
    if (raw.bill) usedIn.push("Bills");
    changes.push(`Use in: ${usedIn.length > 0 ? usedIn.join(" & ") : "None"}`);

    // For edit, list the individual field-level changes as well
    if (
      actionTypes.includes("edit") &&
      changeDetails.field_changes &&
      changeDetails.field_changes.length > 0
    ) {
      const fieldLabels: Record<string, string> = {
        account_name: "Account Name",
        account_type: "Account Type",
        parent_account_id: "Parent Account",
        invoice: "Use in Invoices",
        bill: "Use in Bills",
      };

      changes.push("");
      changes.push("Changes:");
      changeDetails.field_changes.forEach(
        (fc: { field: string; old: string; new: string }) => {
          const label = fieldLabels[fc.field] ?? fc.field;
          let oldVal = fc.old;
          let newVal = fc.new;
          if (fc.field === "account_type") {
            oldVal = fmtType(oldVal);
            newVal = fmtType(newVal);
          }
          if (fc.field === "invoice" || fc.field === "bill") {
            oldVal = oldVal === "true" ? "Yes" : "No";
            newVal = newVal === "true" ? "Yes" : "No";
          }
          changes.push(`  ${label}: ${oldVal} → ${newVal}`);
        },
      );
    }

    return changes;
  };

  /**
   * Format change details for Customer
   * Shows customer information, contact details, address, and financial information
   * Exported for use in ApprovalDetailPanel
   */
  const formatCustomerChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const rawJson = changeDetails.raw_json;

    if ((actionCode || "").toLowerCase() === "bulk_upload") {
      const rows = (rawJson as { rows?: unknown }).rows;
      const n = Array.isArray(rows) ? rows.length : 0;
      if (n > 0) {
        changes.push(
          `Bulk upload: ${n} row(s) will be created here after your manager approves.`,
        );
      } else {
        changes.push("Bulk upload pending review.");
      }
      return changes;
    }

    // Delete review: show concise summary only (no Zoho IDs, no empty sections)
    const actionTypesLocal = changeDetails.action_types || [];
    const isDeleteRequest =
      actionCode?.toLowerCase() === "delete" ||
      actionTypesLocal.includes("delete");
    if (isDeleteRequest) {
      changes.push("Action: Delete Customer");
      if (rawJson.customer_name) {
        changes.push(`Name: ${formatValue(rawJson.customer_name)}`);
      }
      changes.push("");
      changes.push(
        "⚠ This customer will be permanently removed from Zoho Books.",
      );
      return changes;
    }

    // Customer Information
    changes.push("Customer Information:");
    if (rawJson.customer_name) {
      changes.push(`  Name: ${formatValue(rawJson.customer_name)}`);
    }
    if (rawJson.gst_treatment) {
      changes.push(`  GST Treatment: ${formatValue(rawJson.gst_treatment)}`);
    }
    if (rawJson.gstin_vat_tax_id) {
      changes.push(`  GSTIN: ${rawJson.gstin_vat_tax_id.toUpperCase()}`);
    }
    if (rawJson.pan_tax_registration_no) {
      changes.push(`  PAN: ${rawJson.pan_tax_registration_no.toUpperCase()}`);
    }

    // Contact Information
    changes.push("");
    changes.push("Contact Information:");
    if (rawJson.contact_name_1) {
      changes.push(`  Primary Contact: ${formatValue(rawJson.contact_name_1)}`);
    }
    if (rawJson.contact_number_1) {
      changes.push(`  Phone: ${rawJson.contact_number_1}`);
    }
    if (rawJson.contact_email_1) {
      changes.push(`  Email: ${rawJson.contact_email_1.toLowerCase()}`);
    }
    if (rawJson.contact_name_2) {
      changes.push(
        `  Secondary Contact: ${formatValue(rawJson.contact_name_2)}`,
      );
    }
    if (rawJson.contact_number_2) {
      changes.push(`  Secondary Phone: ${rawJson.contact_number_2}`);
    }
    if (rawJson.contact_email_2) {
      changes.push(
        `  Secondary Email: ${rawJson.contact_email_2.toLowerCase()}`,
      );
    }
    if (rawJson.communication_email_id) {
      changes.push(
        `  Communication Email: ${rawJson.communication_email_id.toLowerCase()}`,
      );
    }

    // Address Information
    changes.push("");
    changes.push("Address:");
    if (rawJson.billing_address) {
      changes.push(`  Billing: ${formatValue(rawJson.billing_address)}`);
    }
    if (rawJson.shipping_address) {
      changes.push(`  Shipping: ${formatValue(rawJson.shipping_address)}`);
    }
    if (rawJson.city) {
      changes.push(`  City: ${formatValue(rawJson.city)}`);
    }
    if (rawJson.state) {
      changes.push(`  State: ${rawJson.state.toUpperCase()}`);
    }
    if (rawJson.country) {
      changes.push(`  Country: ${rawJson.country.toUpperCase()}`);
    }
    if (rawJson.postal_code) {
      changes.push(`  Postal Code: ${rawJson.postal_code}`);
    }

    // Financial Information
    changes.push("");
    changes.push("Financial Information:");
    if (rawJson.currency_name || rawJson.currency_code) {
      const currencyDisplay = rawJson.currency_name
        ? `${rawJson.currency_name}${
            rawJson.currency_code ? ` (${rawJson.currency_code})` : ""
          }`
        : rawJson.currency_code;
      changes.push(`  Currency: ${currencyDisplay}`);
    }
    if (rawJson.payment_terms !== undefined && rawJson.payment_terms !== null) {
      changes.push(`  Payment Terms: ${rawJson.payment_terms} days`);
    }
    if (rawJson.credit_limit) {
      changes.push(`  Credit Limit: ${rawJson.credit_limit}`);
    }
    if (rawJson.bank_account_details) {
      changes.push(`  Bank Account: ${rawJson.bank_account_details}`);
    }

    // Other Information
    if (rawJson.status) {
      changes.push("");
      changes.push(`Status: ${formatValue(rawJson.status)}`);
    }

    return changes;
  };

  /**
   * Format change details for Vendor
   * Shows vendor information, contact details, compliance, banking, and taxation details
   * Exported for use in ApprovalDetailPanel
   */
  const formatVendorChanges = (): string[] => {
    const changes: string[] = [];
    if (!changeDetails?.raw_json) return changes;

    const rawJson = changeDetails.raw_json;

    if ((actionCode || "").toLowerCase() === "bulk_upload") {
      const rows = (rawJson as { rows?: unknown }).rows;
      const n = Array.isArray(rows) ? rows.length : 0;
      if (n > 0) {
        changes.push(
          `Bulk upload: ${n} row(s) will be created here after your manager approves.`,
        );
      } else {
        changes.push("Bulk upload pending review.");
      }
      return changes;
    }

    // Delete review: show concise summary only (no Zoho IDs, no empty sections)
    const actionTypesLocal = changeDetails.action_types || [];
    const isDeleteRequest =
      actionCode?.toLowerCase() === "delete" ||
      actionTypesLocal.includes("delete");
    if (isDeleteRequest) {
      changes.push("Action: Delete Vendor");
      const name =
        rawJson.vendor_legal_name ?? rawJson.vendor_name ?? rawJson.name;
      if (name) {
        changes.push(`Name: ${formatValue(name)}`);
      }
      changes.push("");
      changes.push(
        "⚠ This vendor will be permanently removed from Zoho Books.",
      );
      return changes;
    }

    // Vendor Information
    changes.push("Vendor Information:");
    if (rawJson.vendor_legal_name) {
      changes.push(`  Legal Name: ${formatValue(rawJson.vendor_legal_name)}`);
    }
    if (rawJson.vendor_legal_entity) {
      changes.push(
        `  Legal Entity: ${formatValue(rawJson.vendor_legal_entity)}`,
      );
    }
    if (rawJson.trading_name) {
      changes.push(`  Trading Name: ${formatValue(rawJson.trading_name)}`);
    }
    if (rawJson.type_of_vendor) {
      changes.push(`  Type: ${formatValue(rawJson.type_of_vendor)}`);
    }
    if (rawJson.business_category) {
      changes.push(
        `  Business Category: ${formatValue(rawJson.business_category)}`,
      );
    }
    if (rawJson.vendor_classification) {
      changes.push(
        `  Classification: ${formatValue(rawJson.vendor_classification)}`,
      );
    }

    // Address Details
    changes.push("");
    changes.push("Address Details:");
    if (rawJson.registered_office_address) {
      changes.push(
        `  Registered Office: ${formatValue(rawJson.registered_office_address)}`,
      );
    }
    if (rawJson.correspondence_address) {
      changes.push(
        `  Correspondence: ${formatValue(rawJson.correspondence_address)}`,
      );
    }
    if (rawJson.city) {
      changes.push(`  City: ${formatValue(rawJson.city)}`);
    }
    if (rawJson.state) {
      changes.push(`  State: ${rawJson.state.toUpperCase()}`);
    }
    if (rawJson.country) {
      changes.push(`  Country: ${rawJson.country.toUpperCase()}`);
    }
    if (rawJson.postal_code) {
      changes.push(`  Postal Code: ${rawJson.postal_code}`);
    }

    // Contact Information
    changes.push("");
    changes.push("Contact Information:");
    if (rawJson.primary_contact_name) {
      changes.push(
        `  Primary Contact: ${formatValue(rawJson.primary_contact_name)}`,
      );
    }
    if (rawJson.primary_contact_phone) {
      changes.push(`  Phone: ${rawJson.primary_contact_phone}`);
    }
    if (rawJson.primary_contact_email) {
      changes.push(`  Email: ${rawJson.primary_contact_email.toLowerCase()}`);
    }
    if (rawJson.alternate_contact) {
      changes.push(
        `  Alternate Contact: ${formatValue(rawJson.alternate_contact)}`,
      );
    }
    if (rawJson.escalation_contact) {
      changes.push(
        `  Escalation Contact: ${formatValue(rawJson.escalation_contact)}`,
      );
    }
    if (rawJson.website) {
      changes.push(`  Website: ${rawJson.website.toLowerCase()}`);
    }

    // Compliance & Registration
    changes.push("");
    changes.push("Compliance & Registration:");
    if (rawJson.pan_tax_id) {
      changes.push(`  PAN: ${rawJson.pan_tax_id.toUpperCase()}`);
    }
    if (rawJson.gstin) {
      changes.push(`  GSTIN: ${rawJson.gstin.toUpperCase()}`);
    }
    if (rawJson.msme_flag) {
      changes.push(
        `  MSME: ${
          rawJson.msme_flag === "Y"
            ? "Yes"
            : rawJson.msme_flag === "N"
              ? "No"
              : formatValue(rawJson.msme_flag)
        }`,
      );
    }
    if (rawJson.company_registration_number) {
      changes.push(
        `  Company Registration: ${rawJson.company_registration_number.toUpperCase()}`,
      );
    }
    if (rawJson.import_export_code) {
      changes.push(
        `  Import/Export Code: ${rawJson.import_export_code.toUpperCase()}`,
      );
    }
    if (rawJson.udyam_number) {
      changes.push(`  Udyam Number: ${rawJson.udyam_number.toUpperCase()}`);
    }

    // Banking Details
    changes.push("");
    changes.push("Banking Details:");
    if (rawJson.beneficiary_name) {
      changes.push(`  Beneficiary: ${formatValue(rawJson.beneficiary_name)}`);
    }
    if (rawJson.bank_account_number) {
      changes.push(`  Account Number: ${rawJson.bank_account_number}`);
    }
    if (rawJson.bank_name) {
      changes.push(`  Bank: ${formatValue(rawJson.bank_name)}`);
    }
    if (rawJson.branch) {
      changes.push(`  Branch: ${formatValue(rawJson.branch)}`);
    }
    if (rawJson.ifsc_swift_code) {
      changes.push(`  IFSC/SWIFT: ${rawJson.ifsc_swift_code.toUpperCase()}`);
    }
    if (rawJson.beneficiary_address) {
      changes.push(
        `  Beneficiary Address: ${formatValue(rawJson.beneficiary_address)}`,
      );
    }

    // Payment & Financial Terms
    changes.push("");
    changes.push("Payment & Financial Terms:");
    if (rawJson.currency_name || rawJson.currency_code) {
      const currencyDisplay = rawJson.currency_name
        ? `${rawJson.currency_name}${
            rawJson.currency_code ? ` (${rawJson.currency_code})` : ""
          }`
        : rawJson.currency_code;
      changes.push(`  Currency: ${currencyDisplay}`);
    }
    if (rawJson.payment_terms !== undefined && rawJson.payment_terms !== null) {
      changes.push(`  Payment Terms: ${rawJson.payment_terms} days`);
    }
    if (rawJson.preferred_payment_method) {
      changes.push(
        `  Payment Method: ${formatValue(rawJson.preferred_payment_method)}`,
      );
    }
    if (rawJson.credit_limit) {
      changes.push(`  Credit Limit: ${rawJson.credit_limit}`);
    }
    if (rawJson.advance_security_deposit) {
      changes.push(`  Security Deposit: ${rawJson.advance_security_deposit}`);
    }

    // Taxation
    changes.push("");
    changes.push("Taxation:");
    if (rawJson.gst_category) {
      changes.push(`  GST Category: ${formatValue(rawJson.gst_category)}`);
    }
    if (rawJson.gst_treatment) {
      changes.push(`  GST Treatment: ${formatValue(rawJson.gst_treatment)}`);
    }
    if (rawJson.tds_applicable) {
      changes.push(
        `  TDS Applicable: ${
          rawJson.tds_applicable === "Y"
            ? "Yes"
            : rawJson.tds_applicable === "N"
              ? "No"
              : rawJson.tds_applicable
        }`,
      );
    }

    if (rawJson.tds_name) {
      changes.push(`  TDS Tax Rate: ${rawJson.tds_name}`);
    }
    if (rawJson.party_type) {
      changes.push(`  Party Type: ${formatValue(rawJson.party_type)}`);
    }

    // Other Details
    if (rawJson.risk_rating) {
      changes.push("");
      changes.push(`Risk Rating: ${formatValue(rawJson.risk_rating)}`);
    }
    if (rawJson.invoice_submission_method) {
      changes.push(
        `Invoice Submission: ${formatValue(rawJson.invoice_submission_method)}`,
      );
    }
    if (rawJson.status) {
      changes.push(`Status: ${formatValue(rawJson.status)}`);
    }

    return changes;
  };

  // ============================================================================
  // MAIN FORMATTER - Routes to module-specific formatter
  // ============================================================================

  /**
   * Format change details for display - one change per line for better UX
   * Routes to appropriate formatter based on moduleCode
   */
  const formatChangeDetails = (): string | null => {
    if (!changeDetails) return null;

    // For customer, use special formatter
    if (moduleCode?.toLowerCase() === "customer") {
      const customerChanges = formatCustomerChanges();
      return customerChanges.length > 0 ? customerChanges.join("\n") : null;
    }

    // For vendor, use special formatter
    if (moduleCode?.toLowerCase() === "vendor") {
      const vendorChanges = formatVendorChanges();
      return vendorChanges.length > 0 ? vendorChanges.join("\n") : null;
    }

    // For monthly_checklist, use special formatter
    if (moduleCode?.toLowerCase() === "monthly_checklist") {
      const checklistChanges = formatMonthEndChecklistChanges();
      return checklistChanges.length > 0 ? checklistChanges.join("\n") : null;
    }

    // For bank_transaction, use special formatter
    if (moduleCode?.toLowerCase() === "bank_transaction") {
      const bankChanges = formatBankTransactionChanges();
      return bankChanges.length > 0 ? bankChanges.join("\n") : null;
    }

    // For bank_account, use special formatter (create bank/credit card account)
    if (moduleCode?.toLowerCase() === "bank_account") {
      const bankAccountChanges = formatBankAccountChanges();
      return bankAccountChanges.length > 0
        ? bankAccountChanges.join("\n")
        : null;
    }

    // For gl, use GL-specific formatter
    if (moduleCode?.toLowerCase() === "gl") {
      const glChanges = formatGlChanges();
      return glChanges.length > 0 ? glChanges.join("\n") : null;
    }

    // Handle legacy old_value/new_value format (skip for bank_transaction)
    const legacyChanges: string[] = [];
    if (changeDetails.old_value && changeDetails.new_value) {
      // For advance payment, show specially
      if (
        changeDetails.old_value === "0" &&
        changeDetails.new_value &&
        !isNaN(Number(changeDetails.new_value)) &&
        Number(changeDetails.new_value) > 0
      ) {
        legacyChanges.push(`Advance of ${changeDetails.new_value} applied`);
      } else {
        legacyChanges.push(
          `Old: ${changeDetails.old_value} → New: ${changeDetails.new_value}`,
        );
      }
    }

    // Invoice write off / cancel write off: show total, paid, remaining (no field_changes needed)
    const moduleCodeLower = moduleCode?.toLowerCase();
    const actionCodeLower = actionCode?.toLowerCase();
    if (
      moduleCodeLower === "invoice" &&
      (actionCodeLower === "write_off" ||
        actionCodeLower === "cancel_write_off") &&
      changeDetails?.raw_json
    ) {
      const r = changeDetails.raw_json;
      const sym = r.currency_symbol ?? "₹";
      const total =
        r.total != null
          ? typeof r.total === "number"
            ? `${sym}${Number(r.total).toFixed(2)}`
            : `${sym}${r.total}`
          : "—";
      const remaining =
        r.balance != null
          ? typeof r.balance === "number"
            ? `${sym}${Number(r.balance).toFixed(2)}`
            : `${sym}${r.balance}`
          : "—";
      const paid =
        r.paid != null
          ? typeof r.paid === "number"
            ? `${sym}${Number(r.paid).toFixed(2)}`
            : `${sym}${r.paid}`
          : r.total != null && r.balance != null
            ? `${sym}${(Number(r.total) - Number(r.balance)).toFixed(2)}`
            : "—";
      const lines = [
        "Invoice:",
        `  Number: ${r.invoice_number ?? "—"}`,
        `  Total: ${total}`,
        `  Paid: ${paid}`,
        `  Remaining: ${remaining}`,
      ];
      return lines.join("\n");
    }

    // Handle field_changes based on module
    if (changeDetails.field_changes && changeDetails.field_changes.length > 0) {
      // Route to appropriate formatter based on module
      if (
        moduleCodeLower === "credit_note" ||
        moduleCodeLower === "debit_note"
      ) {
        const headerSection = formatCreditDebitNoteHeaderFromRawJson(
          changeDetails.raw_json,
        );
        const creditDebitChanges = formatCreditDebitNoteChanges(
          changeDetails.field_changes,
        );
        const lineItemsSection = formatAllLineItemsFromRawJson(
          changeDetails.raw_json,
          moduleCode,
        );
        const allChanges = [
          ...legacyChanges,
          ...(headerSection.length > 0 ? ["", ...headerSection] : []),
          ...creditDebitChanges,
          ...lineItemsSection,
        ];
        return allChanges.length > 0 ? allChanges.join("\n") : null;
      } else if (
        moduleCodeLower === "journal" ||
        moduleCodeLower === "ap_ar_journal"
      ) {
        // Draft flow: from saved-as-draft journal (push, create_recurring). Always show updated journal from raw_json + recurring when present.
        const actionTypes = changeDetails.action_types || [];
        const isJournalDraftFlow =
          (actionTypes.includes("push_to_zoho") ||
            actionTypes.includes("push_to_zoho_without_attachment") ||
            actionTypes.includes("create_recurring") ||
            actionTypes.includes("ap_ar_create") ||
            actionTypes.includes("ap_ar_delete")) &&
          !actionTypes.includes("update_after_push");

        if (isJournalDraftFlow && changeDetails.raw_json) {
          const journalLines = formatJournalHeaderAndLineItemsFromRawJson(
            changeDetails.raw_json,
          );
          const recurringLines = hasRecurringParamsInRawJson(
            changeDetails.raw_json,
          )
            ? formatRecurringJournalFieldsOnly(changeDetails.raw_json)
            : [];
          const allLines = [
            ...legacyChanges,
            ...journalLines,
            ...(recurringLines.length > 0 ? ["", ...recurringLines] : []),
          ];
          return allLines.length > 0 ? allLines.join("\n") : null;
        }

        // update_after_push or other: use field_changes (Needs approval diff)
        const journalChanges = formatJournalChanges(
          changeDetails.field_changes,
          changeDetails.raw_json,
        );
        const allChanges = [...legacyChanges, ...journalChanges];
        return allChanges.length > 0 ? allChanges.join("\n") : null;
      } else if (
        (moduleCodeLower === "invoice" || moduleCodeLower === "bill") &&
        actionCodeLower === "override_gl_permanent"
      ) {
        // For invoices and bills with GL override, use special formatter
        // This handles both scenarios:
        // 1. Only GL overrides in field_changes → shows only GL overrides
        // 2. GL overrides + other changes in field_changes → shows both sections
        const glOverrideFormatted = formatInvoiceBillChangesWithGLOverride(
          changeDetails.field_changes,
          changeDetails,
        );
        if (glOverrideFormatted) {
          // Show only the diff – do not append full line items when we have field_changes
          const hasFieldChanges =
            (changeDetails.field_changes?.length ?? 0) > 0;
          const lineItemsSection = hasFieldChanges
            ? []
            : formatAllLineItemsFromRawJson(changeDetails.raw_json, moduleCode);
          const combined = [
            glOverrideFormatted,
            ...(lineItemsSection.length > 0 ? lineItemsSection : []),
          ];
          return combined.join("\n");
        }
        // Fallback: if no GL overrides found but we're in override_gl_permanent mode,
        // still return null (no changes to show)
        return null;
      } else {
        // For invoices and bills without GL override, use existing logic
        const kind = moduleCodeLower === "invoice" ? "invoice" : "bill";
        const { systemChanges, userChanges } =
          formatInvoiceBillReviewSummarySections(kind, changeDetails, taxes);
        const invoiceBillChanges = userChanges;
        const hasFieldChanges = (changeDetails.field_changes?.length ?? 0) > 0;
        // For update_after_push with original_json: show full document (before changes) then Changes Requested diff
        const hasOriginalJson =
          changeDetails.original_json &&
          typeof changeDetails.original_json === "object" &&
          Object.keys(changeDetails.original_json).length > 0;
        if (
          hasFieldChanges &&
          hasOriginalJson &&
          (moduleCodeLower === "invoice" || moduleCodeLower === "bill")
        ) {
          const documentBeforeChanges =
            formatInvoiceBillCreateSummaryFromRawJson(
              changeDetails.original_json,
              moduleCodeLower,
            );
          const documentAfterChanges =
            formatInvoiceBillCreateSummaryFromRawJson(
              changeDetails.raw_json,
              moduleCodeLower,
            );
          // For action type "edit", do not show Customer GSTIN / Vendor GSTIN change lines in Changes Requested
          const actionTypes = changeDetails.action_types || [];
          const isEditAction = actionTypes.includes("edit");
          const changesRequestedLines = isEditAction
            ? invoiceBillChanges.filter(
                (line) =>
                  !line.startsWith("Customer GSTIN changed") &&
                  !line.startsWith("Vendor GSTIN changed") &&
                  !line.startsWith("Customer GSTIN:") &&
                  !line.startsWith("Vendor GSTIN:"),
              )
            : [...invoiceBillChanges];
          // Include new total (after TCS / before TDS for bills) in Changes Requested when it differs from before
          const oldTotal = getEffectiveTotalFromRawJson(
            changeDetails.original_json,
            moduleCodeLower,
          );
          const newTotal = getEffectiveTotalFromRawJson(
            changeDetails.raw_json,
            moduleCodeLower,
          );
          // When TCS is applied on the new document, show TCS Total in Changes Requested so reviewer sees the breakdown
          const rawJson = changeDetails.raw_json;
          if (
            rawJson?.tcs_tax_id &&
            rawJson.tcs_tax_id !== "" &&
            Number(rawJson?.tcs_tax_percentage) > 0
          ) {
            const sub = Number(rawJson.sub_total) || 0;
            const tax = Number(rawJson.tax_total) || 0;
            const tcsAmt =
              ((sub + tax) * (Number(rawJson.tcs_tax_percentage) || 0)) / 100;
            const tcsLabel = rawJson.tcs_tax_name
              ? `TCS Total (${rawJson.tcs_tax_name})`
              : "TCS Total";
            changesRequestedLines.push(`${tcsLabel}: ${tcsAmt.toFixed(2)}`);
          }
          if (
            Number.isFinite(oldTotal) &&
            Number.isFinite(newTotal) &&
            Math.abs(oldTotal - newTotal) > 1e-6
          ) {
            changesRequestedLines.push(
              `Total: ${oldTotal.toFixed(2)} → ${newTotal.toFixed(2)}`,
            );
          }
          const allChanges = [
            ...legacyChanges,
            "Document (before changes):",
            "",
            ...documentBeforeChanges,
            "",
            ...(systemChanges.length > 0
              ? ["System-applied updates:", "", ...systemChanges, ""]
              : []),
            "Changes Requested:",
            "",
            ...changesRequestedLines,
            ...(documentAfterChanges.length > 0
              ? ["", "Document (after changes):", "", ...documentAfterChanges]
              : []),
          ];
          return allChanges.length > 0 ? allChanges.join("\n") : null;
        }
        // Show only the diff when we have field_changes and no original_json; else append full line items from raw_json
        const lineItemsSection = hasFieldChanges
          ? []
          : formatAllLineItemsFromRawJson(changeDetails.raw_json, moduleCode);
        const allChanges = [
          ...legacyChanges,
          ...(systemChanges.length > 0
            ? ["System-applied updates:", "", ...systemChanges, ""]
            : []),
          ...invoiceBillChanges,
          ...lineItemsSection,
        ];
        return allChanges.length > 0 ? allChanges.join("\n") : null;
      }
    }

    // If only legacy format exists
    if (legacyChanges.length > 0) {
      return legacyChanges.join("\n");
    }

    // Journal draft flow with no or minimal field_changes: show full journal from raw_json + recurring when present
    const moduleCodeLowerForJournal = moduleCode?.toLowerCase();
    if (
      (moduleCodeLowerForJournal === "journal" ||
        moduleCodeLowerForJournal === "ap_ar_journal") &&
      changeDetails.raw_json
    ) {
      const actionTypes = changeDetails.action_types || [];
      const isJournalDraftFlowNoFieldChanges =
        (actionTypes.includes("push_to_zoho") ||
          actionTypes.includes("push_to_zoho_without_attachment") ||
          actionTypes.includes("create_recurring") ||
          actionTypes.includes("ap_ar_create") ||
          actionTypes.includes("ap_ar_delete")) &&
        !actionTypes.includes("update_after_push");
      if (isJournalDraftFlowNoFieldChanges) {
        const journalLines = formatJournalHeaderAndLineItemsFromRawJson(
          changeDetails.raw_json,
        );
        const recurringLines = hasRecurringParamsInRawJson(
          changeDetails.raw_json,
        )
          ? formatRecurringJournalFieldsOnly(changeDetails.raw_json)
          : [];
        const allLines = [
          ...legacyChanges,
          ...journalLines,
          ...(recurringLines.length > 0 ? ["", ...recurringLines] : []),
        ];
        return allLines.length > 0 ? allLines.join("\n") : null;
      }
    }

    // For invoice/bill/credit_note/debit_note with no field_changes, still show document + line items from raw_json
    if (
      moduleCodeLower === "invoice" ||
      moduleCodeLower === "bill" ||
      moduleCodeLower === "credit_note" ||
      moduleCodeLower === "debit_note"
    ) {
      // Create action for manual invoice/bill: show exact current document (details + line items + totals).
      // No "from → to" diff: always updated changes only (incl. resubmission after rejection).
      const actionTypes = changeDetails.action_types || [];
      const isCreateInvoiceOrBill =
        (moduleCodeLower === "invoice" || moduleCodeLower === "bill") &&
        (actionCode?.toLowerCase() === "create" ||
          actionTypes.includes("create"));
      if (isCreateInvoiceOrBill && changeDetails.raw_json) {
        const allowlisted = formatInvoiceBillAllowlistedCreateSummaryLines(
          moduleCodeLower === "bill" ? "bill" : "invoice",
          changeDetails.raw_json as Record<string, unknown>,
          taxes,
        );
        if (allowlisted.length > 0) {
          return allowlisted.join("\n");
        }
        const createSummary = formatInvoiceBillCreateSummaryFromRawJson(
          changeDetails.raw_json,
          moduleCodeLower,
        );
        if (createSummary.length > 0) return createSummary.join("\n");
      }
      // Delete after push to Zoho (invoice/bill): show full document details so reviewer sees what will be deleted
      const isDeleteAfterPushInvoiceOrBill =
        (moduleCodeLower === "invoice" || moduleCodeLower === "bill") &&
        (actionCode?.toLowerCase() === "delete_after_push_to_zoho" ||
          actionTypes.includes("delete_after_push_to_zoho"));
      if (isDeleteAfterPushInvoiceOrBill && changeDetails.raw_json) {
        const deleteSummary = formatInvoiceBillCreateSummaryFromRawJson(
          changeDetails.raw_json,
          moduleCodeLower,
        );
        if (deleteSummary.length > 0) return deleteSummary.join("\n");
      }

      const headerSection =
        moduleCodeLower === "credit_note" || moduleCodeLower === "debit_note"
          ? formatCreditDebitNoteHeaderFromRawJson(changeDetails.raw_json)
          : [];
      const lineItemsSection = formatAllLineItemsFromRawJson(
        changeDetails.raw_json,
        moduleCode,
      );
      if (headerSection.length > 0 || lineItemsSection.length > 0) {
        const parts = [
          ...(headerSection.length > 0 ? headerSection : []),
          ...(lineItemsSection.length > 0 ? lineItemsSection : []),
        ];
        return parts.join("\n");
      }
    }

    return null;
  };

  const changeDisplay = formatChangeDetails();
  const isBulkUploadReview =
    (actionCode || "").toLowerCase() === "bulk_upload" ||
    Boolean(changeDetails?.action_types?.includes("bulk_upload"));
  const isMonthEndChecklist = moduleCode?.toLowerCase() === "monthly_checklist";
  const checklistData = isMonthEndChecklist ? changeDetails?.raw_json : null;
  const isGLOverrideReview =
    actionCode?.toLowerCase() === "override_gl_permanent" ||
    changeDetails?.action_types?.includes("override_gl_permanent") ||
    changeDetails?.action_types?.includes("override_gl_once");
  const glOverrideReasonEntries: Array<{
    lineItemNumber: number;
    reason: string;
  }> = (() => {
    const lineItemGLData = changeDetails?.line_item_gl_data;

    if (Array.isArray(lineItemGLData)) {
      return lineItemGLData
        .filter(
          (entry: any) =>
            entry?.line_item_index !== undefined && entry?.reason?.trim(),
        )
        .sort((a: any, b: any) => a.line_item_index - b.line_item_index)
        .map((entry: any) => ({
          lineItemNumber: entry.line_item_index + 1,
          reason: entry.reason.trim(),
        }));
    }

    if (
      changeDetails?.reason?.trim() &&
      changeDetails?.line_item_index !== undefined
    ) {
      return [
        {
          lineItemNumber: changeDetails.line_item_index + 1,
          reason: changeDetails.reason.trim(),
        },
      ];
    }

    return [];
  })();

  // Calculate dynamic height based on content
  const calculateModalHeight = () => {
    let height = 400; // Base height
    if (isMonthEndChecklist && checklistData) {
      // Add space for title, description, attachments, notes
      height += 200;
      if (checklistData.attachments && checklistData.attachments.length > 0) {
        height += checklistData.attachments.length * 60; // ~60px per attachment
      }
      if (checklistData.notes) {
        height += 80;
      }
    } else if (changeDisplay) {
      const lines = changeDisplay.split("\n").length;
      height += Math.min(lines * 20, 300); // Add up to 300px for changes
    }
    if (changeDetails?.reason) {
      height += 80; // Add space for reason
    }
    return `${Math.min(height, 800)}px`; // Max 800px
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <Dialog.Content
        className="min-w-[500px] max-w-[600px]"
        style={{ height: calculateModalHeight() }}
      >
        <div className="flex w-full flex-col items-start gap-6 bg-white px-8 py-8 pb-12">
          {/* Header */}
          <div className="flex w-full items-center justify-between">
            <span className="text-heading-2 font-heading-2 text-default-font">
              {title ?? "Request Review"}
            </span>
            <IconButton
              icon={<FeatherX />}
              onClick={handleClose}
              disabled={isSubmitting}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="w-full rounded-md bg-error-50 border border-error-200 px-4 py-3">
              <span className="text-body font-body text-error-700">
                {error}
              </span>
            </div>
          )}

          {/* Closed month indicator */}
          {(changeDetails?.closed_month === true ||
            changeDetails?.action_types?.includes("month_close")) && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-neutral-100 px-4 py-3 border border-solid border-neutral-200">
              <span className="text-body-bold font-body-bold text-neutral-700">
                Closed month
              </span>
              <span className="text-body font-body text-neutral-700">
                This request is for a change in a closed month and requires
                review and approval.
              </span>
            </div>
          )}

          {/* Action Request - Post-close change first, then other actions. Fallback to single actionCode when action_types not provided (e.g. create_recurring from MakeRecurringModal). */}
          {((changeDetails?.action_types &&
            changeDetails.action_types.length > 0) ||
            actionCode) && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-info-50 px-4 py-3 border border-solid border-info-200">
              <span className="text-body-bold font-body-bold text-info-700">
                Action Request
              </span>
              <div className="flex flex-col items-start gap-1">
                {(changeDetails?.action_types?.length
                  ? sortActionTypesForDisplay(changeDetails.action_types)
                  : [actionCode]
                ).map((action, index) => {
                  let formattedAction = actionLabels[action] || action;
                  if (
                    entityType === "bank_account" &&
                    changeDetails?.raw_json
                  ) {
                    const raw = changeDetails.raw_json as {
                      account_type?: unknown;
                    };
                    const isCredit =
                      raw.account_type === "credit" ||
                      raw.account_type === "credit_card";
                    if (action === "create") {
                      formattedAction = isCredit
                        ? "Create Credit Card Account"
                        : "Create Bank Account";
                    } else if (action === "update") {
                      formattedAction = isCredit
                        ? "Update Credit Card Account"
                        : "Update Bank Account";
                    } else if (action === "delete") {
                      formattedAction = isCredit
                        ? "Delete Credit Card Account"
                        : "Delete Bank Account";
                    }
                  }
                  if (entityType === "gl") {
                    if (action === "create") formattedAction = "Create a GL";
                    else if (action === "edit" || action === "update")
                      formattedAction = "Update GL";
                    else if (action === "delete") formattedAction = "Delete GL";
                  }
                  if (
                    (entityType === "customer" || entityType === "vendor" || entityType === "gl") &&
                    action === "bulk_upload"
                  ) {
                    formattedAction =
                      entityType === "customer"
                        ? "Bulk upload Customers from Excel"
                        : entityType === "vendor"
                          ? "Bulk upload Vendors from Excel"
                          : "Bulk upload GLs from Excel";
                  }
                  return (
                    <span
                      key={index}
                      className="text-body font-body text-info-700"
                    >
                      • {formattedAction}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* Month End Checklist Task View */}
          {isMonthEndChecklist && checklistData ? (
            <div className="flex w-full flex-col items-start gap-4 rounded-md bg-neutral-50 px-4 py-4 border border-solid border-neutral-200">
              {/* Title */}
              {checklistData.item_title && (
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-body font-body text-subtext-color">
                    Title
                  </span>
                  <span className="text-body-bold font-body-bold text-default-font">
                    {checklistData.item_title}
                  </span>
                </div>
              )}

              {/* Description */}
              {checklistData.item_description && (
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-body font-body text-subtext-color">
                    Description
                  </span>
                  <span className="text-body font-body text-default-font">
                    {checklistData.item_description}
                  </span>
                </div>
              )}

              {/* Attachments */}
              {checklistData.attachments &&
                Array.isArray(checklistData.attachments) &&
                checklistData.attachments.length > 0 && (
                  <div className="flex w-full flex-col items-start gap-2">
                    <span className="text-body font-body text-subtext-color">
                      Supporting Documentation
                    </span>
                    <div className="flex w-full flex-col items-start gap-2">
                      {checklistData.attachments.map(
                        (att: any, index: number) => {
                          const fileName =
                            att.original_filename ||
                            att.file_name ||
                            `Attachment ${index + 1}`;
                          return (
                            <div
                              key={att.id || index}
                              className="flex w-full items-center gap-3 rounded-md bg-white px-3 py-3 border border-solid border-neutral-200"
                            >
                              <FeatherFile className="text-heading-3 font-heading-3 text-neutral-600" />
                              <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
                                <span className="text-body font-body text-default-font">
                                  {fileName}
                                </span>
                                {att.uploaded_at && (
                                  <span className="text-caption font-caption text-subtext-color">
                                    Uploaded{" "}
                                    {new Date(att.uploaded_at).toLocaleString(
                                      "en-US",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        hour: "numeric",
                                        minute: "2-digit",
                                      },
                                    )}
                                  </span>
                                )}
                              </div>
                              {att.s3_url && (
                                <IconButton
                                  variant="neutral-tertiary"
                                  size="small"
                                  icon={<FeatherDownload />}
                                  onClick={() => {
                                    window.open(att.s3_url, "_blank");
                                  }}
                                />
                              )}
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                )}

              {/* Notes */}
              {checklistData.notes && (
                <div className="flex w-full flex-col items-start gap-1">
                  <span className="text-body font-body text-subtext-color">
                    Notes
                  </span>
                  <div className="flex w-full flex-col items-start gap-2 rounded-md bg-white px-3 py-3 border border-solid border-neutral-200">
                    <span className="text-body font-body text-default-font whitespace-pre-line">
                      {checklistData.notes}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Change Details Display (for other entity types). Hide when no changes (disableSubmit) to avoid showing summary + "No Changes Detected" together. */
            changeDisplay &&
            !disableSubmit && (
              <div
                className={`flex w-full flex-col items-start rounded-md bg-neutral-50 px-4 py-3 border border-solid border-neutral-200 ${
                  actionCode?.toLowerCase() === "create_recurring"
                    ? "gap-1"
                    : "gap-3"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    Changes Summary
                  </span>
                </div>
                <div className="flex w-full flex-col items-start gap-2 max-h-[300px] overflow-y-auto">
                  <div className="text-body font-body text-default-font whitespace-pre-line text-sm leading-relaxed">
                    {typeof changeDisplay === "string"
                      ? changeDisplay.trimStart()
                      : changeDisplay}
                  </div>
                </div>
              </div>
            )
          )}

          {/* No Changes Warning */}
          {disableSubmit && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-warning-50 px-4 py-3 border border-solid border-warning-200">
              <span className="text-body-bold font-body-bold text-warning-700">
                No Changes Detected
              </span>
              <span className="text-body font-body text-warning-700">
                Please make changes to the document before requesting a review.
              </span>
            </div>
          )}

          {/* Reason Display (if provided and NOT a GL override) */}
          {changeDetails?.reason && !isGLOverrideReview && (
            <div className="flex w-full flex-col items-start gap-2 rounded-md bg-brand-50 px-4 py-3 border border-solid border-brand-200">
              <span className="text-body-bold font-body-bold text-brand-700">
                Reason for Change
              </span>
              <span className="text-body font-body text-brand-700">
                {changeDetails.reason}
              </span>
            </div>
          )}

          {/* GL Override Reason Display (supports multiple line items) */}
          {isGLOverrideReview &&
            glOverrideReasonEntries.map((entry) => (
              <div
                key={entry.lineItemNumber}
                className="flex w-full flex-col items-start gap-2 rounded-md bg-brand-50 px-4 py-3 border border-solid border-brand-200"
              >
                <span className="text-body-bold font-body-bold text-brand-700">
                  Reason for GL Override - Line Item {entry.lineItemNumber}
                </span>
                <span className="text-body font-body text-brand-700">
                  {entry.reason}
                </span>
              </div>
            ))}

          {/* Entity Info (without link) — hidden for bulk import reviews */}
          {!isBulkUploadReview ? (
          <div className="flex w-full items-center">
            <span className="text-body font-body text-subtext-color">
              {entityType === "bank_transaction" && transactionData
                ? `Reviewing: Bank Transaction - ${new Date(
                    transactionData.date,
                  ).toLocaleDateString()} - ${
                    transactionData.description
                  } - ₹${transactionData.amount.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`
                : entityType === "bank_account" && changeDetails?.raw_json
                  ? (() => {
                      const isCreditCard =
                        changeDetails.raw_json.account_type === "credit";
                      const label = isCreditCard
                        ? "Create Credit Card Account"
                        : "Create Bank Account";
                      const name =
                        changeDetails.raw_json.account_name || "New account";
                      return `Reviewing: ${label} – ${name}`;
                    })()
                  : entityType === "gl" && changeDetails?.raw_json
                    ? (() => {
                        const glRaw = changeDetails.raw_json as Record<
                          string,
                          unknown
                        >;
                        const actionTypes = changeDetails.action_types || [];
                        const verb = actionTypes.includes("delete")
                          ? "Delete"
                          : actionTypes.includes("edit")
                            ? "Update"
                            : "Create";
                        const glName = glRaw.account_name ?? entityId;
                        return glName != null && String(glName).trim() !== ""
                          ? `Reviewing: ${verb} GL – ${String(glName)}`
                          : `Reviewing: ${verb} GL`;
                      })()
                    : (entityType === "journal" ||
                          entityType === "ap_ar_journal") &&
                        documentNumber
                      ? `Journal # ${documentNumber}`
                      : `Reviewing: ${formatValue(entityType)} ${
                          documentNumber ? `#${documentNumber}` : `#${entityId}`
                        }`}
            </span>
          </div>
          ) : null}

          {/* Comments TextArea */}
          <TextArea
            className="h-auto w-full flex-none"
            variant="filled"
            label="Comments"
            helpText={
              isMonthEndChecklist
                ? "Add context or explanation for the reviewer"
                : "Please explain why this change requires review"
            }
            error={!!error && !comments.trim()}
          >
            <TextArea.Input
              placeholder={
                isMonthEndChecklist
                  ? "Add context or explanation for the reviewer... (Required)"
                  : "Enter your comments here... (Required)"
              }
              value={comments}
              onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                setComments(event.target.value);
                setError(null);
              }}
              rows={4}
            />
          </TextArea>

          {/* Action Buttons */}
          <div className="flex w-full items-center justify-end gap-2 border-t border-solid border-neutral-border pt-6">
            <Button
              variant="neutral-tertiary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="brand-primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !comments.trim() || disableSubmit}
            >
              {isSubmitting ? "Submitting..." : "Submit for Review"}
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
